import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectClinicProfile,
  selectHasPermission,
  selectClinicSharesTreatments,
  selectClinicSharesAllPatients,
  selectClinicRole,
} from "../../store/slices/authSlice";
import {
  useGetTreatmentQuery,
  useGetTreatmentsForPatientQuery,
  useCreateTreatmentMutation,
  useUpdateTreatmentMutation,
  useDeleteTreatmentMutation,
  useSoftDeleteTreatmentMutation,
  useAddTreatmentCommentMutation,
  useGetTreatmentTagsQuery,
  useLogTreatmentViewMutation,
  useLogTreatmentReadAuditMutation,
  useGetTreatmentVersionsQuery,
} from "../../store/services/treatmentsApi";
import { useGetPatientQuery } from "../../store/services/patientsApi";
import { useGetTemplatesQuery } from "../../store/services/formsApi";
import {
  useGetCategoriesQuery,
  useCreateTaskMutation,
} from "../../store/services/tasksApi";
import { useToast } from "../../contexts/ToastContext";
import {
  useGetAppointmentQuery,
  useGetPatientAppointmentsWithoutTreatmentQuery,
  useUpdateAppointmentMutation,
} from "../../store/services/calendarApi";
import {
  useGetTreatmentFilesQuery,
  useUploadPatientFileMutation,
  useDeletePatientFileMutation,
} from "../../store/services/patientFilesApi";
import {
  useGetClinicGamesQuery,
  getGoalsByIds,
} from "../../store/services/gamesApi";
import { useGetLiveSessionsForPatientQuery } from "../../store/services/liveSessionsApi";
import {
  MAX_FILE_SIZE_MB,
  getFileTypeCategory,
  isFilePreviewable,
  generateTreatmentSummary,
  enrichTreatmentSummary,
  markLiveSessionAsImported,
  updatePatientSummary,
  rebuildPatientSummary,
  generateInitialPatientSummary,
  updatePatient as updatePatientDoc,
  getPatient,
  aiTreatmentChat,
  updateTreatmentAiConversation,
} from "../../firebase";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import {
  ArrowRight,
  Clock,
  Lock,
  LockOpen,
  Plus,
  ChatCircle,
  Tag,
  ArrowClockwise,
  FloppyDisk,
  Check,
  FileText,
  Stethoscope,
  WarningCircle,
  CalendarBlank,
  LinkBreak,
  LinkSimple,
  Trash,
  Paperclip,
  X,
  File,
  Image,
  FilePdf,
  FileDoc,
  FileXls,
  DownloadSimple,
  Eye,
  Folder,
  Sparkle,
  Camera,
  ImageSquare,
  GameController,
  MagicWand,
  Warning,
  ArrowLeft,
  PencilSimple,
  ClockCounterClockwise,
  DotsThreeVertical,
  CaretDown,
  CheckSquare,
  Copy,
  User,
  Prohibit,
} from "@phosphor-icons/react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { HelpButton } from "../../components/shared";
import { NoAccessState, ReadOnlyBanner } from "../../components/shared/access";
import GlobalLoader from "../../components/shared/GlobalLoader";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import TimePicker from "../../components/ui/TimePicker";
import DatePicker from "../../components/ui/DatePicker";
import ModalCloseButton from "../../components/ui/ModalCloseButton";
import CustomClinikitActionButton from "../../components/ui/CustomClinikitActionButton";
import "./TreatmentDocPage.css";
import RichTextEditor from "../../components/ui/RichTextEditor";
import AiChatDialog from "../../components/treatments/AiChatDialog";
import { getIsraelDateString } from "../../utils/dateUtils";

// Field types for templates
const FIELD_TYPES = {
  short_text: { rows: 1, className: "" },
  long_text: { rows: 4, className: "" },
  date: { type: "date" },
  checkbox: { type: "checkbox" },
  select: { type: "select" },
  radio: { type: "radio" },
  multi_select: { type: "multi_select" },
  section_header: { type: "section_header" },
};

// Default template (when no template selected)
const DEFAULT_TEMPLATE = {
  id: "default",
  name: "תבנית בסיסית",
  fields: [
    { id: "title", label: "כותרת", type: "short_text", required: true },
    {
      id: "content",
      label: "תוכן הטיפול",
      type: "long_text",
      required: true,
    },
  ],
};

// Build a fresh copy of the default template's fields with empty values.
// Used whenever a new-treatment form is initialized so the basic template
// is always pre-selected (per product spec).
const buildDefaultTemplateFields = () =>
  DEFAULT_TEMPLATE.fields.map((f) => ({ ...f, value: "" }));

// Strip HTML tags / &nbsp; and trim — "does this rich-text value hold real text?"
const stripToText = (v) =>
  String(v || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

// True when the therapist actually typed something into the template fields.
// Module scope (pure) so save closures can use it without dependency churn.
const fieldsHaveTypedContent = (fields) =>
  (fields || []).some((f) => {
    // Section headers are visual dividers — never "content".
    if (f.type === "section_header") return false;
    if (f.type === "checkbox") return f.value === true || f.value === "true";
    if (f.type === "multi_select") return (f.value || []).length > 0;
    return stripToText(f.value) !== "";
  });

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  return `${formatDate(dateStr)} ${formatTime(dateStr)}`;
};

// Calculate age from birth date
const calculatePatientAge = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

function TreatmentDocPage() {
  const { patientId, treatmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  // Permissions v2 — legacy therapists resolve viewSensitive/write TRUE,
  // viewAllClinic FALSE, so today's UI is unchanged for them.
  const canViewSensitive = useSelector(selectHasPermission("clinical.viewSensitive"));
  const canWriteTreatments = useSelector(selectHasPermission("treatments.write"));
  const canExportData = useSelector(selectHasPermission("allowExportData"));
  const canViewAllTreatments = useSelector(selectHasPermission("treatments.viewAllClinic"));
  // Appointments are readable by calendar.viewClinic holders / all-mode members
  // (rules), independent of treatment visibility.
  const canViewClinicCalendar = useSelector(selectHasPermission("calendar.viewClinic"));
  const sharesAllPatients = useSelector(selectClinicSharesAllPatients);
  // Multi-clinic v2 Phase 3: "all" sharing mode with treatments included —
  // every active member reads every report. Legacy clinics resolve false.
  const sharesTreatments = useSelector(selectClinicSharesTreatments);
  const clinicRole = useSelector(selectClinicRole);
  const clinicProfile = useSelector(selectClinicProfile);
  const isSpeechTherapist = clinicProfile?.type === "speech_therapist";

  // Determine if creating new or editing existing
  const isNew = treatmentId === "new";
  const appointmentIdFromQuery = searchParams.get("appointmentId");
  // Arrived to document a specific appointment (homepage pending-actions,
  // carousel, calendar, diary) — the link is fixed and can't be changed.
  const isAppointmentLocked = isNew && Boolean(appointmentIdFromQuery);

  // Form state — the basic template is pre-selected so every new treatment
  // opens with ready-to-fill fields, regardless of entry point (calendar,
  // diary, carousel, direct URL, etc.). Live-session auto-import and the
  // existing-treatment loader both overwrite this later when applicable.
  const [formData, setFormData] = useState({
    templateId: "default",
    treatmentTypeId: "",
    treatmentDate: getIsraelDateString(new Date()),
    treatmentTime: "", // Empty by default - user must fill manually
    tagIds: [],
    title: "",
    fields: buildDefaultTemplateFields(),
    content: "",
    appointmentId: null,
    selectedGameIds: [],
  });
  const [newComment, setNewComment] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  // Soft delete (locked reports only) — see softDeleteTreatment in firebase.js
  const [showConfirmSoftDelete, setShowConfirmSoftDelete] = useState(false);
  const [softDeleteReason, setSoftDeleteReason] = useState("");
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [aiProcessingStatus, setAiProcessingStatus] = useState(null); // null | 'processing' | 'done'
  // Summary popup — single unified modal that replaces the old cluster of
  // header buttons (save / delete / AI / lock). Opens when the therapist
  // clicks the primary "שמור סיכום טיפול" CTA; from there they can review,
  // run AI enrichment, and lock & save.
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryModalView, setSummaryModalView] = useState("summary"); // 'summary' | 'ai-loading' | 'ai-preview' | 'ai-error'
  const [aiPreviewTab, setAiPreviewTab] = useState("enriched"); // 'enriched' | 'original'
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  // Mobile header — timeline bottom sheet + overflow (⋯) menu. Presentational
  // only: the sheet reuses the same timeline list the desktop sidebar renders.
  const [showTimelineSheet, setShowTimelineSheet] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);

  // Lock background scroll while the timeline sheet is open (mobile —
  // the page scrolls on the body there).
  useEffect(() => {
    if (!showTimelineSheet) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showTimelineSheet]);
  const autoSaveTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  // Whether the linked appointment was already flagged as summarized this
  // session — prevents re-writing the flags on every 60s autosave (each
  // updateAppointment call logs activity and reschedules cloud tasks).
  const summaryMarkedRef = useRef(false);
  const [linkingMode, setLinkingMode] = useState("select"); // "select" | "manual"
  // Guided setup for new treatments: 1 = link appointment, 2 = pick template,
  // 3 = setup done, full form visible. Existing treatments always render as 3.
  const [setupStep, setSetupStep] = useState(1);
  // Once setup finished at least once, reopening a step becomes an "edit"
  // (a close button appears and selections return straight to the form).
  const [hasCompletedSetup, setHasCompletedSetup] = useState(false);
  // Template staged behind a confirmation when switching would erase
  // content the therapist already typed.
  const [pendingTemplateChange, setPendingTemplateChange] = useState(null);

  // Files state
  const [showFilesSection, setShowFilesSection] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]); // Files to upload with treatment
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Edit locked treatment state
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);
  const preEditSnapshotRef = useRef(null);

  // AI Enrichment state — consumed by the summary popup (ai-preview view).
  // Note: the view/phase is driven by `summaryModalView`; enrichment data
  // and error messages are kept separately.
  const [aiEnrichedData, setAiEnrichedData] = useState(null); // { enrichedFields, originalFields }
  const [editableEnrichedFields, setEditableEnrichedFields] = useState(null);
  const [aiEnrichmentError, setAiEnrichmentError] = useState(null);
  const [aiRetryAvailable, setAiRetryAvailable] = useState(true);

  // Quick actions (FAB) — compact menu inside the form area for two flows:
  // (1) create a new task without leaving this page, (2) copy the patient's
  // previous session summary into the current form.
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showQuickTaskModal, setShowQuickTaskModal] = useState(false);
  const [quickTaskForm, setQuickTaskForm] = useState({
    title: "",
    note: "",
    status: "todo",
    dueDate: "",
    dueTime: "",
    categoryId: "",
  });
  const [showConfirmCopyPrevious, setShowConfirmCopyPrevious] = useState(false);

  // AI chat — conversation lives in state and is write-behind persisted onto
  // the treatment doc (`aiConversation`) after every message. Autosave never
  // touches that field, so the two write paths can't collide.
  const [showAiChat, setShowAiChat] = useState(false);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [aiChatError, setAiChatError] = useState(null);
  // One-shot hydration guard — autosave invalidates the treatment query, and
  // a refetch mid-chat must not reset the conversation to the stored copy.
  const aiChatHydratedRef = useRef(false);
  // The doc id used for persistence — set synchronously when the first chat
  // message creates the draft, before the route param settles.
  const aiChatTreatmentIdRef = useRef(null);
  // Insert target: the long-text field the therapist last placed a caret in,
  // plus the exact Range, so inserted AI text lands where they were typing.
  const lastFocusedLongTextFieldRef = useRef(null);
  const lastEditorRangeRef = useRef(null);

  // Check if mobile device
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Queries
  const {
    data: patient,
    isLoading: isLoadingPatient,
    error: patientError,
    refetch: refetchPatient,
  } = useGetPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId },
  );

  const {
    data: existingTreatment,
    isLoading: isLoadingTreatment,
    error: treatmentError,
    refetch: refetchTreatment,
  } = useGetTreatmentQuery(
    { clinicId, treatmentId },
    { skip: !clinicId || !treatmentId || isNew },
  );

  // The patient's PRIMARY therapist reads the full history in every clinic
  // (rules allow it); in "all"+treatments sharing mode so does every member.
  const isPrimaryTherapist = !!patient && patient.therapistId === therapistId;
  const canReadAllPatientTreatments =
    isAdmin || canViewAllTreatments || sharesTreatments || isPrimaryTherapist;

  const { data: treatments = [], isLoading: isLoadingTreatments } =
    useGetTreatmentsForPatientQuery(
      {
        clinicId,
        patientId,
        therapistId: canReadAllPatientTreatments ? null : therapistId,
      },
      { skip: !clinicId || !patientId },
    );

  const { data: templates = [] } = useGetTemplatesQuery(
    { clinicId },
    { skip: !clinicId },
  );

  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId },
  );

  // Task categories — only loaded once the user opens the quick-task modal,
  // so we don't fetch them for every treatment-doc page view.
  const { data: taskCategories = [] } = useGetCategoriesQuery(
    { clinicId, type: "task" },
    { skip: !clinicId || !showQuickTaskModal },
  );

  const { data: treatmentTags = [] } = useGetTreatmentTagsQuery(
    { clinicId },
    { skip: !clinicId },
  );

  // Get version history for existing locked treatments
  const { data: treatmentVersions = [] } = useGetTreatmentVersionsQuery(
    { clinicId, treatmentId },
    { skip: !clinicId || !treatmentId || isNew },
  );

  // Get clinic games for game selection
  const { data: clinicGames = [] } = useGetClinicGamesQuery(
    { clinicId },
    { skip: !clinicId },
  );

  // Get recent appointments without treatments (for linking).
  // Appointment rules: owner || admin || co-therapist || calendar.viewClinic ||
  // all-mode — NOT treatments.viewAllClinic, so widen only on those.
  const canReadAllPatientAppointments =
    isAdmin || sharesAllPatients || canViewClinicCalendar;
  const { data: unlinkedAppointments = [] } =
    useGetPatientAppointmentsWithoutTreatmentQuery(
      {
        clinicId,
        patientId,
        therapistId: canReadAllPatientAppointments ? null : therapistId,
      },
      { skip: !clinicId || !patientId || !isNew },
    );

  // When arriving with ?appointmentId=... (e.g. from the calendar right-click
  // menu, pending-appointments carousel, or patient diary), fetch that specific
  // appointment directly. The `unlinkedAppointments` list above is filtered
  // (past month only, must lack a treatmentId, matching therapist, etc.) so a
  // valid source appointment may not be in it — relying on that list caused the
  // auto-link to silently fail. Fetching by id guarantees we can pre-fill.
  const { data: prefilledAppointment } = useGetAppointmentQuery(
    { clinicId, appointmentId: appointmentIdFromQuery },
    { skip: !clinicId || !appointmentIdFromQuery || !isNew },
  );

  // Merge the prefilled appointment into the selection list so it renders as
  // a selectable (and selected) card even if it's not in `unlinkedAppointments`.
  const displayedAppointments = useMemo(() => {
    if (!prefilledAppointment) return unlinkedAppointments;
    const exists = unlinkedAppointments.some(
      (apt) => apt.id === prefilledAppointment.id,
    );
    if (exists) return unlinkedAppointments;
    return [prefilledAppointment, ...unlinkedAppointments];
  }, [unlinkedAppointments, prefilledAppointment]);

  // Get available live sessions for import (only for new treatments).
  // liveSessions rules: owner || admin ONLY — never widen beyond admin.
  const { data: availableLiveSessions = [] } =
    useGetLiveSessionsForPatientQuery(
      {
        clinicId,
        patientId,
        therapistId: isAdmin ? null : therapistId,
      },
      { skip: !clinicId || !patientId || !isNew },
    );

  // Id of a live session whose data was auto-imported when linking to its
  // appointment. Used at save time to mark the session as imported so it
  // won't be offered again.
  const [importedLiveSessionId, setImportedLiveSessionId] = useState(null);
  // When the user tries to link an appointment that has a live session but
  // the form already contains work, we stage the link here and ask for
  // confirmation before overwriting.
  const [pendingAppointmentLink, setPendingAppointmentLink] = useState(null);
  // Toggles the collapsible preview of the live session's content inside
  // the confirm dialog.
  const [showPendingLivePreview, setShowPendingLivePreview] = useState(false);

  // Mutations
  const [createTreatment, { isLoading: isCreating }] =
    useCreateTreatmentMutation();
  const [updateTreatment, { isLoading: isUpdating }] =
    useUpdateTreatmentMutation();
  const [deleteTreatment, { isLoading: isDeleting }] =
    useDeleteTreatmentMutation();
  const [softDeleteTreatment, { isLoading: isSoftDeleting }] =
    useSoftDeleteTreatmentMutation();
  const [addComment, { isLoading: isAddingComment }] =
    useAddTreatmentCommentMutation();
  const [updateAppointment] = useUpdateAppointmentMutation();
  const [logTreatmentView] = useLogTreatmentViewMutation();
  const [logTreatmentReadAudit] = useLogTreatmentReadAuditMutation();
  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();

  const { showSuccess, showError } = useToast();

  // Files queries and mutations
  const { data: treatmentFiles = [] } = useGetTreatmentFilesQuery(
    { clinicId, patientId, treatmentId },
    { skip: !clinicId || !patientId || !treatmentId || isNew },
  );
  const [uploadFile, { isLoading: isUploadingFile }] =
    useUploadPatientFileMutation();
  const [deletePatientFile] = useDeletePatientFileMutation();

  // isLocked should always be false when creating new treatment
  const isLocked = !isNew && existingTreatment?.status === "locked";
  // Soft-deleted report: read-only, no actions, red banner (no restore)
  const isDeleted = !isNew && existingTreatment?.isDeleted === true;
  // Author or admin only — mirrors firestore.rules (treatments update: ownsResource || admin).
  // Co-therapists can read a locked report but never edit it.
  const isTreatmentOwner =
    isAdmin ||
    existingTreatment?.therapistId === therapistId ||
    (Array.isArray(existingTreatment?.therapistIds) &&
      existingTreatment.therapistIds.includes(therapistId));
  // Someone else's report opened by a viewer who can read it (co-therapist /
  // treatments.viewAllClinic) — strictly read-only: no autosave, no save /
  // lock / comment actions.
  const isReadOnlyView = !isNew && Boolean(existingTreatment) && !isTreatmentOwner;
  const canSoftDelete = isLocked && !isEditing && !isDeleted && isTreatmentOwner;
  // "ייצוא PDF": any viewer who may read this locked report + export permission
  // (the Cloud Function re-checks canReadTreatment + allowExportData).
  const canExportPdf = isLocked && !isEditing && !isDeleted && canExportData;
  const exportPath = `/dashboard/patients/${patientId}/treatments/${treatmentId}/export`;
  const canEditLocked = isLocked && !isEditing && !isDeleted && isTreatmentOwner;
  const isFieldsDisabled = (isLocked && !isEditing) || isReadOnlyView;
  const isSaving = isCreating || isUpdating;

  // Visual-only required-field tracking: which sections/fields are missing
  // required input. Never blocks save — cues only.
  const missingRequired = useMemo(() => {
    if (isFieldsDisabled) return { details: false, templateFieldIds: [] };
    const isRichTextEmpty = (v) =>
      !v ||
      !String(v)
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();
    const details = !formData.treatmentTime;
    const templateFieldIds = (formData.fields || [])
      .filter((f) => {
        if (!f.required) return false;
        if (f.type === "checkbox" || f.type === "section_header") return false;
        if (f.type === "long_text") return isRichTextEmpty(f.value);
        if (f.type === "multi_select")
          return !Array.isArray(f.value) || f.value.length === 0;
        return !f.value || !String(f.value).trim();
      })
      .map((f) => f.id);
    return { details, templateFieldIds };
  }, [formData.treatmentTime, formData.fields, isFieldsDisabled]);

  // Setup wizard visibility + the currently linked appointment (for the
  // editable summary chips shown once setup is done).
  const showSetupWizard = isNew && !isLocked && setupStep < 3;
  const linkedAppointment = useMemo(
    () =>
      displayedAppointments.find((apt) => apt.id === formData.appointmentId) ||
      null,
    [displayedAppointments, formData.appointmentId],
  );
  const hasVersionHistory =
    treatmentVersions.length > 0 ||
    (existingTreatment?.currentVersion || 1) > 1;

  // Seed the summarized guard from the linked appointment when we have its
  // data (only fetched while isNew). When editing after a reload the data
  // isn't in scope — worst case is one extra idempotent flag write.
  useEffect(() => {
    if (linkedAppointment?.isTherapistSummarizedTheMeeting === true) {
      summaryMarkedRef.current = true;
    }
  }, [linkedAppointment]);

  // Reset form data when switching to new treatment. Matches the initial
  // useState seed so the basic template is also pre-selected here.
  useEffect(() => {
    if (isNew) {
      setFormData({
        templateId: "default",
        treatmentTypeId: "",
        treatmentDate: getIsraelDateString(new Date()),
        treatmentTime: "", // Empty by default - user must fill manually
        tagIds: [],
        title: "",
        fields: buildDefaultTemplateFields(),
        content: "",
        appointmentId: null,
        selectedGameIds: [],
      });
      setLinkingMode("select");
      setSetupStep(1);
      setHasCompletedSetup(false);
      setPendingTemplateChange(null);
      setImportedLiveSessionId(null);
      setPendingAppointmentLink(null);
      lastSavedDataRef.current = null;
      summaryMarkedRef.current = false;
      setAutoSaveStatus(null);
    }
  }, [isNew]);

  // Auto-advance the setup wizard when an appointment gets linked while on
  // step 1 (query-param prefill, or the live-session confirm dialog). Only on
  // an actual change so navigating back to step 1 doesn't bounce forward.
  const prevSetupAppointmentIdRef = useRef(null);
  useEffect(() => {
    const prev = prevSetupAppointmentIdRef.current;
    prevSetupAppointmentIdRef.current = formData.appointmentId;
    if (
      isNew &&
      setupStep === 1 &&
      formData.appointmentId &&
      formData.appointmentId !== prev
    ) {
      // First run continues to template selection; when re-linking after
      // setup is done, go straight back to the form.
      setSetupStep(hasCompletedSetup ? 3 : 2);
    }
  }, [isNew, setupStep, formData.appointmentId, hasCompletedSetup]);

  // Auto-switch to manual mode when there's nothing to link against. Skip this
  // when we arrived with an explicit ?appointmentId — in that case we want to
  // stay in "select" mode so the prefilled appointment can be auto-selected
  // (otherwise we'd flicker to "manual" before the auto-select effect runs).
  useEffect(() => {
    if (
      isNew &&
      !appointmentIdFromQuery &&
      unlinkedAppointments.length === 0 &&
      linkingMode === "select"
    ) {
      setLinkingMode("manual");
    }
  }, [isNew, appointmentIdFromQuery, unlinkedAppointments.length, linkingMode]);

  // Auto-select appointment from query parameter (from calendar right-click,
  // PendingAppointmentsCarousel, patient diary, etc.). Uses `prefilledAppointment`
  // fetched by id so this works regardless of whether the appointment meets the
  // filters of `unlinkedAppointments`. When the target appointment has a
  // non-imported live session, its template + fields are auto-imported at the
  // same time — no confirmation needed here because the form is empty.
  useEffect(() => {
    if (
      isNew &&
      appointmentIdFromQuery &&
      prefilledAppointment &&
      !formData.appointmentId // Only run if not already selected
    ) {
      const aptDate = new Date(prefilledAppointment.startTime);
      setFormData((prev) => ({
        ...prev,
        appointmentId: prefilledAppointment.id,
        treatmentDate: getIsraelDateString(aptDate),
        treatmentTime: aptDate.toLocaleTimeString("he-IL", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        treatmentTypeId:
          prefilledAppointment.treatmentTypeId || prev.treatmentTypeId,
      }));
      setLinkingMode("select");

      const liveSession = availableLiveSessions.find(
        (session) => session.appointmentId === prefilledAppointment.id,
      );
      if (liveSession) {
        setFormData((prev) => ({
          ...prev,
          templateId:
            liveSession.templateId === "ai_enriched"
              ? "default"
              : liveSession.templateId || "default",
          fields: liveSession.fields || [],
          selectedGameIds: liveSession.selectedGameIds || [],
          treatmentTypeId: liveSession.treatmentTypeId || prev.treatmentTypeId,
        }));
        setImportedLiveSessionId(liveSession.id);
      }
    }
  }, [
    isNew,
    appointmentIdFromQuery,
    prefilledAppointment,
    formData.appointmentId,
    availableLiveSessions,
  ]);

  // Initialize form data when treatment loads
  useEffect(() => {
    if (existingTreatment && !isNew) {
      const treatmentDateTime = existingTreatment.treatmentDate
        ? new Date(existingTreatment.treatmentDate)
        : new Date();
      setFormData({
        templateId: existingTreatment.templateId || null,
        treatmentTypeId: existingTreatment.treatmentTypeId || "",
        treatmentDate: getIsraelDateString(treatmentDateTime),
        treatmentTime: treatmentDateTime.toLocaleTimeString("he-IL", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        tagIds: existingTreatment.tagIds || [],
        title: existingTreatment.title || "",
        fields: existingTreatment.fields || [],
        content: existingTreatment.content || "",
        appointmentId: existingTreatment.appointmentId || null,
        selectedGameIds: existingTreatment.selectedGameIds || [],
      });
    }
  }, [existingTreatment, isNew]);

  // Reset the AI chat when navigating to a different report. React Router
  // reuses this component across `:treatmentId` changes, so without this the
  // previous report's conversation leaks into the newly opened one.
  // Skip the `new → createdId` route swap (first chat message creates the
  // draft and points the ref at the new id) — that must keep the conversation.
  useEffect(() => {
    if (aiChatTreatmentIdRef.current === treatmentId) return;
    aiChatHydratedRef.current = false;
    aiChatTreatmentIdRef.current = null;
    setAiChatMessages([]);
    setAiChatError(null);
  }, [treatmentId]);

  // Hydrate the AI chat once from the stored conversation. Later refetches
  // (triggered by autosave cache invalidation) are ignored so an in-progress
  // chat isn't reset to a stale copy.
  useEffect(() => {
    if (existingTreatment && !isNew && !aiChatHydratedRef.current) {
      aiChatHydratedRef.current = true;
      if (Array.isArray(existingTreatment.aiConversation)) {
        setAiChatMessages(existingTreatment.aiConversation);
      }
    }
  }, [existingTreatment, isNew]);

  // Track the last caret position inside a long-text editor so "הוסף לדוח"
  // from the AI chat knows where to insert. Refs only — no re-renders while
  // typing. Selections inside the chat dialog don't match `.editor-content`,
  // so they never overwrite the saved target.
  useEffect(() => {
    const handler = () => {
      const sel = document.getSelection();
      if (!sel?.anchorNode || sel.rangeCount === 0) return;
      const el =
        sel.anchorNode.nodeType === 1
          ? sel.anchorNode
          : sel.anchorNode.parentElement;
      const editable = el?.closest?.(".rich-text-editor .editor-content");
      const wrapper = el?.closest?.("[data-treatment-field-id]");
      if (editable && wrapper) {
        lastFocusedLongTextFieldRef.current = wrapper.getAttribute(
          "data-treatment-field-id",
        );
        lastEditorRangeRef.current = sel.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  // Log treatment view for audit trail (Israeli medical data regulations)
  useEffect(() => {
    if (
      !isNew &&
      treatmentId &&
      treatmentId !== "new" &&
      clinicId &&
      therapistId
    ) {
      logTreatmentView({
        clinicId,
        treatmentId,
        therapistId,
        therapistName,
      });
    }
  }, [
    treatmentId,
    isNew,
    clinicId,
    therapistId,
    therapistName,
    logTreatmentView,
  ]);

  // Multi-clinic v2 Phase 3: cross-therapist read audit — once per open of an
  // EXISTING report authored by someone else (the mutation no-ops for the
  // author and debounces repeats client-side).
  const auditedTreatmentIdRef = useRef(null);
  useEffect(() => {
    if (isNew || !clinicId || !therapistId || !existingTreatment?.id) return;
    const ownerTherapistId = existingTreatment.therapistId;
    if (!ownerTherapistId || ownerTherapistId === therapistId) return;
    if (auditedTreatmentIdRef.current === existingTreatment.id) return;
    auditedTreatmentIdRef.current = existingTreatment.id;
    logTreatmentReadAudit({
      clinicId,
      view: {
        treatmentId: existingTreatment.id,
        patientId: existingTreatment.patientId || patientId,
        ownerTherapistId,
        context: "doc",
      },
      viewer: {
        viewerId: therapistId,
        viewerName: therapistName,
        viewerRole: clinicRole,
      },
    });
  }, [
    isNew,
    clinicId,
    therapistId,
    therapistName,
    clinicRole,
    patientId,
    existingTreatment?.id,
    existingTreatment?.therapistId,
    existingTreatment?.patientId,
    logTreatmentReadAudit,
  ]);

  // Set default template if none selected
  useEffect(() => {
    if (isNew && formData.templateId === null && templates.length === 0) {
      setFormData((prev) => ({
        ...prev,
        templateId: "default",
        fields: DEFAULT_TEMPLATE.fields.map((f) => ({
          ...f,
          value: "",
        })),
      }));
    }
  }, [isNew, formData.templateId, templates]);

  // Auto-save for drafts
  const performAutoSave = useCallback(
    async (shouldLock = false) => {
      if (!clinicId || !patientId || !therapistId) return;
      if (isLocked || isReadOnlyView) return;

      // Don't create the treatment while the setup wizard is still open —
      // a linked appointment fills the time, and a 60s autosave would
      // navigate away mid-setup and silently commit the default template.
      if (isNew && setupStep < 3) return;

      // Don't allow saving/locking if time is empty
      if (!formData.treatmentTime) return;

      // Don't auto-save if nothing changed (unless locking)
      const currentDataStr = JSON.stringify(formData);
      if (lastSavedDataRef.current === currentDataStr && !shouldLock) return;

      setAutoSaveStatus("saving");

      try {
        // Combine date and time for treatmentDate
        const treatmentDateTime = formData.treatmentTime
          ? `${formData.treatmentDate}T${formData.treatmentTime}:00`
          : formData.treatmentDate;

        const treatmentData = {
          patientId,
          patientName: patient
            ? `${patient.firstName} ${patient.lastName}`
            : "",
          appointmentId:
            formData.appointmentId || appointmentIdFromQuery || null,
          templateId: formData.templateId,
          treatmentTypeId: formData.treatmentTypeId || null,
          treatmentDate: treatmentDateTime,
          tagIds: formData.tagIds,
          title: formData.title,
          fields: formData.fields,
          content: formData.content,
          summary: "מייצר תקציר פגישה...",
          shortSummary: "מייצר תקציר פגישה...",
          status: shouldLock ? "locked" : "draft",
          selectedGameIds: formData.selectedGameIds || [],
        };

        // The pending-actions "summarized" flags are only earned by a report
        // with real typed content (or an explicit lock) — an empty auto-saved
        // draft must not clear the dashboard step.
        const contentExists =
          fieldsHaveTypedContent(treatmentData.fields) ||
          stripToText(treatmentData.content) !== "";
        const markSummary = shouldLock || contentExists;

        let resultTreatmentId = treatmentId;

        if (isNew || !treatmentId || treatmentId === "new") {
          const result = await createTreatment({
            clinicId,
            treatmentData,
            therapistId,
            therapistName,
          }).unwrap();

          resultTreatmentId = result.id;

          // Update appointment with treatment reference if linked. The link
          // is always written; the summarized flags only when earned.
          const linkedAppointmentId =
            formData.appointmentId || appointmentIdFromQuery;
          if (linkedAppointmentId) {
            await updateAppointment({
              clinicId,
              appointmentId: linkedAppointmentId,
              appointmentData: {
                treatmentId: result.id,
                ...(markSummary
                  ? {
                      isTherapistSummarizedTheMeeting: true,
                      NotifyAboutThisMeetingSummarize: false,
                    }
                  : {}),
              },
              therapistId,
              therapistName,
            }).unwrap();
            if (markSummary) summaryMarkedRef.current = true;
          }

          // Upload pending files
          if (pendingFiles.length > 0) {
            await uploadPendingFiles(result.id);
          }

          // Mark live session as imported if one was auto-linked via the
          // appointment. Using the id (instead of the full session object)
          // keeps us loosely coupled and lets the live sessions list update
          // independently.
          if (importedLiveSessionId) {
            try {
              await markLiveSessionAsImported(
                clinicId,
                importedLiveSessionId,
                result.id,
              );
            } catch (err) {
              console.error(
                "[CK-3304] Failed to mark live session as imported:",
                err,
              );
            }
          }

          // Run AI functions and wait for them to complete
          if (shouldLock) {
            setAiProcessingStatus("processing");

            const aiPromises = [
              // Generate treatment summary
              generateTreatmentSummary({
                clinicId,
                treatmentId: result.id,
              }).catch((err) => {
                console.error(
                  "[CK-3301] AI treatment summary generation failed:",
                  err,
                );
                return { success: false, error: err.message };
              }),
            ];

            // Update/generate patient summary if AI summary is enabled
            if (clinicProfile?.enableAiPatientSummary === true) {
              if (patient?.patientSummary) {
                aiPromises.push(
                  updatePatientSummary({
                    clinicId,
                    patientId,
                    currentSummary: patient.patientSummary,
                    treatmentSummary: formData.content || "לא סופק סיכום טיפול",
                    treatmentFields: formData.fields || [],
                    treatmentDate:
                      formData.treatmentDate || new Date().toISOString(),
                    treatmentTitle:
                      formData.fields?.find((f) => f.id === "title")?.value ||
                      "",
                    currentAge: calculatePatientAge(patient.birthDate),
                  }).catch((err) => {
                    console.error(
                      "[CK-3302] AI patient summary update failed:",
                      err,
                    );
                    return { success: false, error: err.message };
                  }),
                );
              } else {
                aiPromises.push(
                  generateInitialPatientSummary({
                    clinicId,
                    patientData: {
                      age: calculatePatientAge(patient.birthDate),
                      gender: patient.gender || null,
                      languages: patient.languages || [],
                      referralReason: patient.referralReason || null,
                      diagnoses: patient.diagnoses || null,
                      allergies: patient.allergies || null,
                      medications: patient.medications || null,
                      previousTreatments: patient.previousTreatments || null,
                      clinicalNotes: patient.clinicalNotes || null,
                      educationalFramework:
                        patient.educationalFramework || null,
                    },
                  })
                    .then(async (summaryResult) => {
                      if (summaryResult.success && summaryResult.summary) {
                        await updatePatientDoc(clinicId, patientId, {
                          patientSummary: summaryResult.summary,
                        });
                      }
                      return summaryResult;
                    })
                    .catch((err) => {
                      console.error(
                        "[CK-3302] AI initial patient summary failed:",
                        err,
                      );
                      return { success: false, error: err.message };
                    }),
                );
              }
            }

            // Wait for all AI functions to complete
            await Promise.allSettled(aiPromises);

            // Refetch patient to get updated summary
            await refetchPatient?.();

            setAiProcessingStatus("done");

            // Brief delay to show completion before navigating
            await new Promise((resolve) => setTimeout(resolve, 500));
            setAiProcessingStatus(null);
          } else {
            // For drafts, still generate summary in background
            generateTreatmentSummary({
              clinicId,
              treatmentId: result.id,
            }).catch((err) => {
              console.error("[CK-3301] AI summary generation failed:", err);
            });
          }

          // Navigate to the created treatment
          navigate(`/dashboard/patients/${patientId}/treatments/${result.id}`, {
            replace: true,
          });
        } else {
          await updateTreatment({
            clinicId,
            treatmentId,
            treatmentData,
            therapistId,
            therapistName,
          }).unwrap();

          // Update appointment when the link changed, or when the summarized
          // flags are newly earned (content typed / locked) on an appointment
          // this session hasn't flagged yet — e.g. a draft created empty and
          // filled in later.
          const linkedId =
            formData.appointmentId || existingTreatment?.appointmentId || null;
          const linkChanged =
            formData.appointmentId &&
            formData.appointmentId !== existingTreatment?.appointmentId;
          const needMark =
            linkedId && markSummary && !summaryMarkedRef.current;
          if (linkChanged || needMark) {
            await updateAppointment({
              clinicId,
              appointmentId: linkChanged ? formData.appointmentId : linkedId,
              appointmentData: {
                ...(linkChanged ? { treatmentId } : {}),
                ...(markSummary
                  ? {
                      isTherapistSummarizedTheMeeting: true,
                      NotifyAboutThisMeetingSummarize: false,
                    }
                  : {}),
              },
              therapistId,
              therapistName,
            }).unwrap();
            if (markSummary) summaryMarkedRef.current = true;
          }

          // Upload pending files for existing treatment
          if (pendingFiles.length > 0) {
            await uploadPendingFiles(treatmentId);
          }

          // Run AI functions and wait for them when locking
          if (shouldLock) {
            setAiProcessingStatus("processing");

            // Fetch latest patient data to get current summary
            const latestPatient = await getPatient(clinicId, patientId);

            const aiPromises = [
              // Generate treatment summary
              generateTreatmentSummary({
                clinicId,
                treatmentId,
              }).catch((err) => {
                console.error(
                  "[CK-3301] AI treatment summary generation failed:",
                  err,
                );
                return { success: false, error: err.message };
              }),
            ];

            // Update/generate patient summary if AI summary is enabled
            if (clinicProfile?.enableAiPatientSummary === true) {
              if (latestPatient?.patientSummary) {
                aiPromises.push(
                  updatePatientSummary({
                    clinicId,
                    patientId,
                    currentSummary: latestPatient.patientSummary,
                    treatmentSummary: formData.content || "לא סופק סיכום טיפול",
                    treatmentFields: formData.fields || [],
                    treatmentDate:
                      formData.treatmentDate || new Date().toISOString(),
                    treatmentTitle:
                      formData.fields?.find((f) => f.id === "title")?.value ||
                      "",
                    currentAge: calculatePatientAge(latestPatient.birthDate),
                  }).catch((err) => {
                    console.error(
                      "[CK-3302] AI patient summary update failed:",
                      err,
                    );
                    return { success: false, error: err.message };
                  }),
                );
              } else {
                aiPromises.push(
                  generateInitialPatientSummary({
                    clinicId,
                    patientData: {
                      age: calculatePatientAge(latestPatient.birthDate),
                      gender: latestPatient.gender || null,
                      languages: latestPatient.languages || [],
                      referralReason: latestPatient.referralReason || null,
                      diagnoses: latestPatient.diagnoses || null,
                      allergies: latestPatient.allergies || null,
                      medications: latestPatient.medications || null,
                      previousTreatments:
                        latestPatient.previousTreatments || null,
                      clinicalNotes: latestPatient.clinicalNotes || null,
                      educationalFramework:
                        latestPatient.educationalFramework || null,
                    },
                  })
                    .then(async (summaryResult) => {
                      if (summaryResult.success && summaryResult.summary) {
                        await updatePatientDoc(clinicId, patientId, {
                          patientSummary: summaryResult.summary,
                        });
                      }
                      return summaryResult;
                    })
                    .catch((err) => {
                      console.error(
                        "[CK-3302] AI initial patient summary failed:",
                        err,
                      );
                      return { success: false, error: err.message };
                    }),
                );
              }
            }

            // Wait for all AI functions to complete
            await Promise.allSettled(aiPromises);

            // Refetch treatment and patient to get updated summaries from AI
            await Promise.all([refetchTreatment?.(), refetchPatient?.()]);

            setAiProcessingStatus("done");

            // Brief delay to show completion
            await new Promise((resolve) => setTimeout(resolve, 500));
            setAiProcessingStatus(null);
          }
        }

        lastSavedDataRef.current = currentDataStr;
        setAutoSaveStatus("saved");

        // Clear saved status after 3 seconds
        setTimeout(() => setAutoSaveStatus(null), 3000);

        return resultTreatmentId;
      } catch (error) {
        console.error("[CK-3300] Auto-save failed:", {
          errorCode: "CK-3300",
          errorMessage: error.message,
          clinicId,
          patientId,
          treatmentId,
          timestamp: new Date().toISOString(),
        });
        setAutoSaveStatus(null);
      }
    },
    [
      clinicId,
      patientId,
      therapistId,
      therapistName,
      formData,
      isLocked,
      isReadOnlyView,
      isNew,
      setupStep,
      treatmentId,
      patient,
      appointmentIdFromQuery,
      existingTreatment,
      importedLiveSessionId,
      createTreatment,
      updateTreatment,
      updateAppointment,
      navigate,
      refetchTreatment,
      refetchPatient,
    ],
  );

  // Setup auto-save timer (every 60 seconds)
  useEffect(() => {
    if (isLocked) return;

    autoSaveTimerRef.current = setInterval(() => {
      performAutoSave();
    }, 60000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [performAutoSave, isLocked]);

  // Template selection handler
  const handleTemplateSelect = (template) => {
    if (isFieldsDisabled) return;

    setFormData((prev) => ({
      ...prev,
      templateId: template.id,
      fields: template.fields.map((f) => ({
        ...f,
        value: "",
      })),
    }));
  };

  // True when the therapist actually typed something into the template
  // fields (unlike hasUserContent, which also counts the template choice).
  const hasTypedFieldContent = () => fieldsHaveTypedContent(formData.fields);

  // Wizard template pick: same template just continues; a different one is
  // applied immediately when the fields are empty, otherwise it is staged
  // behind a confirmation because switching resets field values.
  const pickTemplate = (template) => {
    if (formData.templateId === template.id) {
      setSetupStep(3);
      setHasCompletedSetup(true);
      return;
    }
    if (hasTypedFieldContent()) {
      setPendingTemplateChange(template);
      return;
    }
    handleTemplateSelect(template);
    setSetupStep(3);
    setHasCompletedSetup(true);
  };

  // Field value change handler
  const handleFieldChange = (fieldId, value) => {
    if (isFieldsDisabled) return;

    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === fieldId ? { ...f, value } : f)),
    }));
  };

  // multi_select stores an array of chosen options — toggle membership.
  const handleMultiSelectToggle = (fieldId, option) => {
    if (isFieldsDisabled) return;

    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => {
        if (f.id !== fieldId) return f;
        const current = Array.isArray(f.value) ? f.value : [];
        const value = current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option];
        return { ...f, value };
      }),
    }));
  };

  // Tag toggle handler
  const handleTagToggle = (tagId) => {
    if (isFieldsDisabled) return;

    setFormData((prev) => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter((id) => id !== tagId)
        : [...prev.tagIds, tagId],
    }));
  };

  // Quick task: open a small modal pre-linked to this patient so the
  // therapist can record a follow-up without leaving the summary form.
  const handleOpenQuickTask = () => {
    setShowQuickActions(false);
    setQuickTaskForm({
      title: patient ? `מעקב — ${patient.firstName} ${patient.lastName}` : "",
      note: "",
      status: "todo",
      dueDate: "",
      dueTime: "",
      categoryId: "",
    });
    setShowQuickTaskModal(true);
  };

  const handleCloseQuickTaskModal = () => {
    if (isCreatingTask) return;
    setShowQuickTaskModal(false);
  };

  const handleSaveQuickTask = async () => {
    const trimmedTitle = quickTaskForm.title.trim();
    if (!trimmedTitle) return;

    try {
      await createTask({
        clinicId,
        taskData: {
          title: trimmedTitle,
          note: quickTaskForm.note.trim() || null,
          status: quickTaskForm.status,
          dueDate: quickTaskForm.dueDate || null,
          dueTime:
            quickTaskForm.dueDate && quickTaskForm.dueTime
              ? quickTaskForm.dueTime
              : null,
          categoryId: quickTaskForm.categoryId || null,
          scope: "personal",
          assignedTo: therapistId,
          relatedPatientId: patientId,
        },
        creatorId: therapistId,
        creatorName: therapistName,
      }).unwrap();
      setShowQuickTaskModal(false);
      showSuccess("המשימה נוצרה בהצלחה");
    } catch (error) {
      console.error("[CK-3260] Error creating quick task from treatment doc:", {
        errorCode: "CK-3260",
        errorMessage: error.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה ביצירת המשימה");
    }
  };

  // Copy previous summary — finds the patient's most recent treatment (other
  // than the one currently being edited) and pulls its template, fields and
  // free-text content into the form. We keep the current date/time/appointment
  // link and selected games untouched. If the form already has user content
  // we ask first, since this overwrites template + fields.
  const previousTreatment = useMemo(() => {
    if (!treatments || treatments.length === 0) return null;
    return (
      treatments.find(
        (t) =>
          t.id !== treatmentId &&
          t.isDeleted !== true &&
          (t.title || t.content || (t.fields && t.fields.length > 0)),
      ) || null
    );
  }, [treatments, treatmentId]);

  const formHasUserContent = useMemo(() => {
    if (formData.title && formData.title.trim()) return true;
    if (formData.content && formData.content.trim()) return true;
    return (formData.fields || []).some((f) => {
      if (f.type === "checkbox") return f.value === true || f.value === "true";
      return f.value && String(f.value).trim().length > 0;
    });
  }, [formData.title, formData.content, formData.fields]);

  const performCopyPrevious = () => {
    if (!previousTreatment) return;
    // If the previous treatment used a template that's no longer available,
    // fall back to "default" so the fields section still renders (fields are
    // driven by formData.fields, but the section only shows when a template
    // resolves).
    const prevTemplateId = previousTreatment.templateId;
    const resolvedTemplateId =
      prevTemplateId === "default" ||
      templates.some((t) => t.id === prevTemplateId)
        ? prevTemplateId || "default"
        : "default";

    setFormData((prev) => ({
      ...prev,
      templateId: resolvedTemplateId,
      title: previousTreatment.title || prev.title,
      fields: (previousTreatment.fields || []).map((f) => ({ ...f })),
      content: previousTreatment.content || "",
      tagIds: previousTreatment.tagIds
        ? [...previousTreatment.tagIds]
        : prev.tagIds,
    }));
    setShowConfirmCopyPrevious(false);
    setShowQuickActions(false);
    showSuccess("הסיכום הקודם הועתק לטופס");
  };

  const handleCopyPrevious = () => {
    setShowQuickActions(false);
    if (!previousTreatment) {
      showError("לא נמצא סיכום קודם להעתקה");
      return;
    }
    if (formHasUserContent) {
      setShowConfirmCopyPrevious(true);
      return;
    }
    performCopyPrevious();
  };

  // Force-sync the live DOM content of each RichTextEditor back into
  // `formData`. The RichTextEditor is contenteditable-based, so its
  // `onChange` only fires on user input events (`onInput`). When a saved
  // draft is loaded, the editor's `innerHTML` is set imperatively from the
  // `value` prop — without firing `onChange`. If the browser normalizes the
  // HTML at that point (e.g. adjusting `<br>` / `&nbsp;`), state can drift
  // from what's actually rendered until the therapist focuses or types.
  // Calling this right before opening the summary popup guarantees the
  // preview shows exactly what's in the form. Returns the freshly synced
  // fields array so callers that need it synchronously (e.g. auto-save
  // closures that see stale state) can use it instead of `formData.fields`.
  const flushRichTextFieldsFromDOM = useCallback(() => {
    const currentFields = formData.fields || [];
    if (currentFields.length === 0) return currentFields;

    let didChange = false;
    const nextFields = currentFields.map((field) => {
      if (field.type !== "long_text") return field;
      const wrapper = document.querySelector(
        `[data-treatment-field-id="${CSS.escape(field.id)}"]`,
      );
      const editable = wrapper?.querySelector(
        ".rich-text-editor .editor-content",
      );
      if (!editable) return field;
      const domValue = editable.innerHTML;
      if (domValue !== (field.value || "")) {
        didChange = true;
        return { ...field, value: domValue };
      }
      return field;
    });

    if (didChange) {
      setFormData((prev) => ({ ...prev, fields: nextFields }));
    }
    return nextFields;
  }, [formData.fields]);

  // --- AI chat -----------------------------------------------------------

  // Persist the conversation onto the treatment doc. Best-effort: the full
  // array is rewritten each time, so a failed write self-heals on the next
  // message.
  const persistAiConversation = async (messages, errorCode) => {
    const docId = aiChatTreatmentIdRef.current;
    if (!docId) return;
    try {
      await updateTreatmentAiConversation(clinicId, docId, messages);
    } catch (error) {
      console.error(`[${errorCode}] Failed to persist AI conversation:`, {
        errorCode,
        errorMessage: error.message,
        clinicId,
        treatmentId: docId,
        messagesCount: messages.length,
        timestamp: new Date().toISOString(),
      });
    }
  };

  // Run one AI turn over the given history and append the answer. Shared by
  // send and retry (retry re-runs the last user message without duplicating it).
  const runAiChatTurn = async (nextMessages, fields) => {
    setAiChatLoading(true);
    try {
      const result = await aiTreatmentChat({
        clinicId,
        patientId,
        currentFormData: { fields },
        messages: nextMessages
          .map(({ role, content }) => ({ role, content }))
          .slice(-12),
      });

      if (!result.success || !result.response) {
        throw new Error(result.error || "AI chat failed");
      }

      const withAssistant = [
        ...nextMessages,
        {
          role: "assistant",
          content: result.response,
          timestamp: new Date().toISOString(),
        },
      ];
      setAiChatMessages(withAssistant);
      trackFeature(FEATURE_KEYS.AI_TREATMENT_CHAT);
      await persistAiConversation(withAssistant, "CK-3331");
    } catch (error) {
      console.error("[CK-3332] AI chat turn failed:", {
        errorCode: "CK-3332",
        errorMessage: error.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      setAiChatError("שגיאה בקבלת תשובה מה-AI");
    } finally {
      setAiChatLoading(false);
    }
  };

  const handleAiChatSend = async (text) => {
    if (aiChatLoading) return;
    setAiChatError(null);

    // Sync unsaved editor content so the AI sees exactly what's typed.
    const flushedFields = flushRichTextFieldsFromDOM();

    const userMsg = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString(),
    };
    const nextMessages = [...aiChatMessages, userMsg];
    setAiChatMessages(nextMessages);

    // The conversation lives on the treatment doc — create the draft first
    // for a brand-new report.
    if (!aiChatTreatmentIdRef.current) {
      if (!isNew && treatmentId && treatmentId !== "new") {
        aiChatTreatmentIdRef.current = treatmentId;
      } else {
        setAiChatLoading(true);
        let createdId;
        try {
          createdId = await performAutoSave(false);
        } catch (error) {
          console.error("[CK-3330] Draft creation for AI chat failed:", {
            errorCode: "CK-3330",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
        }
        if (!createdId || createdId === "new") {
          setAiChatLoading(false);
          setAiChatMessages(aiChatMessages);
          showError("יש למלא שעת טיפול לפני שיחה עם ה-AI");
          return;
        }
        aiChatTreatmentIdRef.current = createdId;
      }
    }

    await persistAiConversation(nextMessages, "CK-3330");
    await runAiChatTurn(nextMessages, flushedFields);
  };

  const handleAiChatRetry = async () => {
    if (aiChatLoading || aiChatMessages.length === 0) return;
    if (aiChatMessages[aiChatMessages.length - 1].role !== "user") return;
    setAiChatError(null);
    const flushedFields = flushRichTextFieldsFromDOM();
    await runAiChatTurn(aiChatMessages, flushedFields);
  };

  // Insert AI text into the report: last-focused long-text field at the saved
  // caret; fallback — first long-text field, appended at the end.
  const handleAiChatInsert = (plainText) => {
    const text = (plainText || "").trim();
    if (!text) return;

    const isLongText = (id) =>
      (formData.fields || []).some(
        (f) => f.id === id && f.type === "long_text",
      );

    let fieldId = lastFocusedLongTextFieldRef.current;
    if (!fieldId || !isLongText(fieldId)) {
      fieldId = (formData.fields || []).find(
        (f) => f.type === "long_text",
      )?.id;
    }
    if (!fieldId) {
      console.error("[CK-3333] No long-text field available for AI insert", {
        errorCode: "CK-3333",
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      showError("לא נמצא שדה טקסט להוספה");
      return;
    }

    const editorEl = document.querySelector(
      `[data-treatment-field-id="${CSS.escape(fieldId)}"] .rich-text-editor .editor-content`,
    );
    if (!editorEl) {
      showError("לא נמצא שדה טקסט להוספה");
      return;
    }

    // Plain text only — build DOM nodes via textContent (no HTML injection).
    const fragment = document.createDocumentFragment();
    text.split(/\n+/).forEach((line) => {
      const div = document.createElement("div");
      div.textContent = line;
      fragment.appendChild(div);
    });

    const savedRange = lastEditorRangeRef.current;
    if (
      savedRange &&
      fieldId === lastFocusedLongTextFieldRef.current &&
      editorEl.contains(savedRange.commonAncestorContainer)
    ) {
      savedRange.collapse(false);
      savedRange.insertNode(fragment);
    } else {
      editorEl.appendChild(fragment);
    }

    handleFieldChange(fieldId, editorEl.innerHTML);
    showSuccess("הטקסט נוסף לדוח");
  };

  // Primary CTA: open the unified summary popup. For a brand-new treatment
  // we silently create the draft first so `treatmentId` exists — this makes
  // AI enrichment and Lock & Save work immediately from inside the popup.
  const handleOpenSummary = async () => {
    if (!formData.treatmentTime) return;

    // Pull the latest DOM content of the rich-text editors into state so the
    // preview reflects everything the user has actually typed — including
    // text that was just auto-populated when the draft loaded.
    flushRichTextFieldsFromDOM();

    const needsDraftCreation = isNew || !treatmentId || treatmentId === "new";

    if (needsDraftCreation) {
      setIsCreatingDraft(true);
      try {
        await performAutoSave(false);
      } catch (error) {
        console.error(
          "[CK-3320] Draft creation for summary popup failed:",
          error,
        );
        setIsCreatingDraft(false);
        return;
      }
      setIsCreatingDraft(false);
    }

    setSummaryModalView("summary");
    setAiPreviewTab("enriched");
    setShowSummaryModal(true);
  };

  // Close the summary popup and reset its transient state. Auto-save keeps
  // the draft persisted in the background — nothing is lost on close.
  const handleCloseSummary = () => {
    setShowSummaryModal(false);
    setSummaryModalView("summary");
    setAiPreviewTab("enriched");
    setAiEnrichedData(null);
    setEditableEnrichedFields(null);
    setAiEnrichmentError(null);
  };

  // "נעל ושמור" clicked from the main summary view. The summary popup is
  // already a clear "review before locking" step, so there's no need for an
  // extra confirmation dialog — we close the popup and lock in one shot.
  const handleLockFromPopup = async () => {
    setShowSummaryModal(false);
    await performAutoSave(true);
  };

  // Lock from the AI preview view. When the enriched tab is active we take
  // the "save original as v1, enriched as current" path; when the original
  // tab is active we just do a regular lock with the therapist's own text.
  const handleLockFromAIPreview = async () => {
    if (aiPreviewTab === "enriched") {
      setShowSummaryModal(false);
      handleAISaveAndLock();
    } else {
      setShowSummaryModal(false);
      await performAutoSave(true);
    }
  };

  // Start editing a locked treatment
  const handleStartEdit = () => {
    if (isDeleted) return;
    if (isLocked && !isTreatmentOwner) {
      showError("רק כותב הדוח או מנהל הקליניקה יכולים לערוך דוח נעול");
      setShowConfirmEdit(false);
      return;
    }
    preEditSnapshotRef.current = { ...formData };
    setIsEditing(true);
    setShowConfirmEdit(false);
  };

  // Cancel editing - restore previous data
  const handleCancelEdit = () => {
    if (preEditSnapshotRef.current) {
      setFormData(preEditSnapshotRef.current);
    }
    setIsEditing(false);
    preEditSnapshotRef.current = null;
  };

  // Save edited locked treatment (creates version + updates)
  const handleSaveLockedEdit = async () => {
    if (!clinicId || !patientId || !therapistId || !treatmentId) return;

    try {
      const treatmentDateTime = formData.treatmentTime
        ? `${formData.treatmentDate}T${formData.treatmentTime}:00`
        : formData.treatmentDate;

      await updateTreatment({
        clinicId,
        treatmentId,
        treatmentData: {
          patientId,
          editLocked: true,
          status: "locked",
          templateId: formData.templateId,
          treatmentTypeId: formData.treatmentTypeId || null,
          treatmentDate: treatmentDateTime,
          tagIds: formData.tagIds,
          title: formData.title,
          fields: formData.fields,
          content: formData.content,
          selectedGameIds: formData.selectedGameIds || [],
        },
        therapistId,
        therapistName,
      }).unwrap();

      setIsEditing(false);
      preEditSnapshotRef.current = null;
    } catch (error) {
      console.error("[CK-3310] Save locked edit failed:", {
        errorCode: "CK-3310",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(
        /permission|insufficient|denied/i.test(error?.message || "")
          ? "אין לך הרשאה לערוך דוח זה"
          : "שגיאה בשמירת העריכה. נסה שוב."
      );
    }
  };

  // AI Enrichment triggered from inside the summary popup. Reuses the same
  // enrichment API; drives `summaryModalView` through loading → preview/error.
  const handleAIEnrichInPopup = async () => {
    if (!clinicId || !patientId) return;

    const textFields = formData.fields.filter(
      (f) =>
        (f.type === "short_text" || f.type === "long_text") &&
        f.value &&
        f.value.trim() !== "",
    );

    if (textFields.length === 0) return;

    setSummaryModalView("ai-loading");
    setAiEnrichmentError(null);
    setAiRetryAvailable(true);

    try {
      const fieldsForApi = textFields.map((f) => ({
        id: f.id,
        label: f.label,
        type: f.type,
        value: f.value,
      }));

      const result = await enrichTreatmentSummary({
        clinicId,
        fields: fieldsForApi,
      });

      if (result.success) {
        setAiEnrichedData({
          enrichedFields: result.enrichedFields,
          originalFields: result.originalFields,
        });
        setEditableEnrichedFields({ ...result.enrichedFields });
        setAiPreviewTab("enriched");
        setSummaryModalView("ai-preview");
      } else {
        setAiEnrichmentError(result.error || "שגיאה בהעשרת הסיכום");
        setSummaryModalView("ai-error");
      }
    } catch (error) {
      console.error("[CK-3305] AI enrichment failed:", error);
      setAiEnrichmentError("שגיאה בהעשרת הסיכום. אנא נסה שוב או פנה לתמיכה.");
      setSummaryModalView("ai-error");
    }
  };

  const handleAIRetryInPopup = async () => {
    setAiRetryAvailable(false);
    await handleAIEnrichInPopup();
  };

  /**
   * Save & Lock from the AI preview modal.
   * 1. Save+lock the treatment with the ORIGINAL therapist text (becomes version 1)
   * 2. Then update (editLocked) with the ENRICHED text (becomes current version)
   */
  const handleAISaveAndLock = async () => {
    if (!editableEnrichedFields || !clinicId || !patientId || !therapistId)
      return;

    setShowSummaryModal(false);
    setAiProcessingStatus("processing");

    try {
      const treatmentDateTime = formData.treatmentTime
        ? `${formData.treatmentDate}T${formData.treatmentTime}:00`
        : formData.treatmentDate;

      // --- Step 1: Save + lock with ORIGINAL text ---
      const originalTreatmentData = {
        patientId,
        patientName: patient ? `${patient.firstName} ${patient.lastName}` : "",
        appointmentId: formData.appointmentId || appointmentIdFromQuery || null,
        templateId: formData.templateId,
        treatmentTypeId: formData.treatmentTypeId || null,
        treatmentDate: treatmentDateTime,
        tagIds: formData.tagIds,
        title: formData.title,
        fields: formData.fields,
        content: formData.content,
        summary: "מייצר תקציר פגישה...",
        shortSummary: "מייצר תקציר פגישה...",
        status: "locked",
        selectedGameIds: formData.selectedGameIds || [],
      };

      let savedTreatmentId = treatmentId;

      if (isNew || !treatmentId || treatmentId === "new") {
        const result = await createTreatment({
          clinicId,
          treatmentData: originalTreatmentData,
          therapistId,
          therapistName,
        }).unwrap();
        savedTreatmentId = result.id;

        const linkedAppointmentId =
          formData.appointmentId || appointmentIdFromQuery;
        if (linkedAppointmentId) {
          await updateAppointment({
            clinicId,
            appointmentId: linkedAppointmentId,
            appointmentData: {
              treatmentId: result.id,
              isTherapistSummarizedTheMeeting: true,
              NotifyAboutThisMeetingSummarize: false,
            },
            therapistId,
            therapistName,
          }).unwrap();
          summaryMarkedRef.current = true;
        }

        if (pendingFiles.length > 0) {
          await uploadPendingFiles(result.id);
        }

        if (importedLiveSessionId) {
          try {
            await markLiveSessionAsImported(
              clinicId,
              importedLiveSessionId,
              result.id,
            );
          } catch (err) {
            console.error(
              "[CK-3304] Failed to mark live session as imported:",
              err,
            );
          }
        }
      } else {
        await updateTreatment({
          clinicId,
          treatmentId: savedTreatmentId,
          treatmentData: originalTreatmentData,
          therapistId,
          therapistName,
        }).unwrap();

        // Locking implies the report is done — flag the linked appointment
        // if this session hasn't yet (e.g. draft was created empty, so the
        // create path skipped the flags).
        const linkedAppointmentId =
          formData.appointmentId || existingTreatment?.appointmentId;
        if (linkedAppointmentId && !summaryMarkedRef.current) {
          await updateAppointment({
            clinicId,
            appointmentId: linkedAppointmentId,
            appointmentData: {
              isTherapistSummarizedTheMeeting: true,
              NotifyAboutThisMeetingSummarize: false,
            },
            therapistId,
            therapistName,
          }).unwrap();
          summaryMarkedRef.current = true;
        }
      }

      // --- Step 2: Update with enriched text (editLocked creates a version of original) ---
      const enrichedFields = formData.fields.map((field) => ({
        ...field,
        value:
          editableEnrichedFields[field.id] !== undefined
            ? editableEnrichedFields[field.id]
            : field.value,
      }));

      await updateTreatment({
        clinicId,
        treatmentId: savedTreatmentId,
        treatmentData: {
          patientId,
          editLocked: true,
          status: "locked",
          templateId: formData.templateId,
          treatmentTypeId: formData.treatmentTypeId || null,
          treatmentDate: treatmentDateTime,
          tagIds: formData.tagIds,
          title: formData.title,
          fields: enrichedFields,
          content: formData.content,
          selectedGameIds: formData.selectedGameIds || [],
        },
        therapistId,
        therapistName,
      }).unwrap();

      // --- Step 3: Run AI summary generation ---
      const aiPromises = [
        generateTreatmentSummary({
          clinicId,
          treatmentId: savedTreatmentId,
        }).catch((err) => {
          console.error(
            "[CK-3301] AI treatment summary generation failed:",
            err,
          );
          return { success: false, error: err.message };
        }),
      ];

      if (clinicProfile?.enableAiPatientSummary === true) {
        const latestPatient = await getPatient(clinicId, patientId);
        if (latestPatient?.patientSummary) {
          aiPromises.push(
            updatePatientSummary({
              clinicId,
              patientId,
              currentSummary: latestPatient.patientSummary,
              treatmentSummary: formData.content || "לא סופק סיכום טיפול",
              treatmentFields: enrichedFields,
              treatmentDate: formData.treatmentDate || new Date().toISOString(),
              treatmentTitle:
                enrichedFields.find((f) => f.id === "title")?.value || "",
              currentAge: calculatePatientAge(latestPatient.birthDate),
            }).catch((err) => {
              console.error("[CK-3302] AI patient summary update failed:", err);
              return { success: false, error: err.message };
            }),
          );
        } else {
          aiPromises.push(
            generateInitialPatientSummary({
              clinicId,
              patientData: {
                age: calculatePatientAge(latestPatient.birthDate),
                gender: latestPatient.gender || null,
                languages: latestPatient.languages || [],
                referralReason: latestPatient.referralReason || null,
                diagnoses: latestPatient.diagnoses || null,
                allergies: latestPatient.allergies || null,
                medications: latestPatient.medications || null,
                previousTreatments: latestPatient.previousTreatments || null,
                clinicalNotes: latestPatient.clinicalNotes || null,
                educationalFramework:
                  latestPatient.educationalFramework || null,
              },
            })
              .then(async (summaryResult) => {
                if (summaryResult.success && summaryResult.summary) {
                  await updatePatientDoc(clinicId, patientId, {
                    patientSummary: summaryResult.summary,
                  });
                }
                return summaryResult;
              })
              .catch((err) => {
                console.error(
                  "[CK-3302] AI initial patient summary failed:",
                  err,
                );
                return { success: false, error: err.message };
              }),
          );
        }
      }

      await Promise.allSettled(aiPromises);
      await Promise.all([refetchTreatment?.(), refetchPatient?.()]);

      setAiProcessingStatus("done");
      await new Promise((resolve) => setTimeout(resolve, 500));
      setAiProcessingStatus(null);

      setAiEnrichedData(null);
      setEditableEnrichedFields(null);

      if (isNew || !treatmentId || treatmentId === "new") {
        navigate(
          `/dashboard/patients/${patientId}/treatments/${savedTreatmentId}`,
          { replace: true },
        );
      }
    } catch (error) {
      console.error("[CK-3305] AI Save & Lock failed:", error);
      setAiProcessingStatus(null);
    }
  };

  // Find a non-imported ended live session that belongs to a given appointment.
  // `availableLiveSessions` is already filtered server-side to ended sessions
  // that haven't been imported yet.
  const getLiveSessionForAppointment = useCallback(
    (appointmentId) => {
      if (!appointmentId) return null;
      return (
        availableLiveSessions.find(
          (session) => session.appointmentId === appointmentId,
        ) || null
      );
    },
    [availableLiveSessions],
  );

  // Detect whether the form already has meaningful user-entered data that
  // would be overwritten by an auto-import of a live session.
  const hasUserContent = useCallback(() => {
    if (formData.templateId) return true;
    if (formData.content && String(formData.content).trim()) return true;
    if (
      Array.isArray(formData.fields) &&
      formData.fields.some(
        (f) => f.value !== "" && f.value !== null && f.value !== undefined,
      )
    ) {
      return true;
    }
    return false;
  }, [formData]);

  // Apply a live session onto the form. Per product decision this copies
  // only the template + fields (+ selected games / treatment type) — it does
  // NOT populate the free-text `content` field and does NOT expose liveNotes.
  const applyLiveSession = useCallback((liveSession) => {
    setFormData((prev) => ({
      ...prev,
      templateId:
        liveSession.templateId === "ai_enriched"
          ? "default"
          : liveSession.templateId || "default",
      fields: liveSession.fields || [],
      selectedGameIds: liveSession.selectedGameIds || [],
      treatmentTypeId: liveSession.treatmentTypeId || prev.treatmentTypeId,
    }));
    setImportedLiveSessionId(liveSession.id);
  }, []);

  // Reset the parts of the form that were populated from a live session.
  const clearImportedLiveSessionData = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      templateId: null,
      fields: [],
      selectedGameIds: [],
    }));
    setImportedLiveSessionId(null);
  }, []);

  // Shared routine that actually links an appointment and, if a live session
  // is provided, imports its template/fields at the same time.
  const performAppointmentLink = useCallback(
    (appointment, liveSession) => {
      const aptDate = new Date(appointment.startTime);
      setFormData((prev) => ({
        ...prev,
        appointmentId: appointment.id,
        treatmentDate: getIsraelDateString(aptDate),
        treatmentTime: aptDate.toLocaleTimeString("he-IL", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        treatmentTypeId: appointment.treatmentTypeId || prev.treatmentTypeId,
      }));
      setLinkingMode("select");

      if (liveSession) {
        applyLiveSession(liveSession);
      } else if (importedLiveSessionId) {
        // Switched to an appointment that doesn't have a live session — drop
        // the template/fields that came from the previous live import so the
        // therapist doesn't end up with mismatched content.
        clearImportedLiveSessionData();
      }
    },
    [applyLiveSession, clearImportedLiveSessionData, importedLiveSessionId],
  );

  // Appointment selection handler — integrates auto-import of the linked
  // live session, with a confirmation step when existing work would be lost.
  const handleAppointmentSelect = (appointment) => {
    if (isFieldsDisabled) return;
    if (formData.appointmentId === appointment.id) return;

    const liveSession = getLiveSessionForAppointment(appointment.id);

    if (liveSession && hasUserContent()) {
      setPendingAppointmentLink({ appointment, liveSession });
      return;
    }

    performAppointmentLink(appointment, liveSession);
  };

  // Confirm: user accepted replacing their current work with the live session.
  const handleConfirmReplaceAndLink = () => {
    if (!pendingAppointmentLink) return;
    const { appointment, liveSession } = pendingAppointmentLink;
    performAppointmentLink(appointment, liveSession);
    setPendingAppointmentLink(null);
    setShowPendingLivePreview(false);
  };

  // Link the appointment but keep the therapist's existing text fields.
  // The live session content is discarded (not applied, not imported).
  // We intentionally bypass performAppointmentLink here so its "clear
  // previous import" branch doesn't run — the therapist explicitly chose
  // to keep what they already typed.
  const handleLinkWithoutReplacing = () => {
    if (!pendingAppointmentLink) return;
    const { appointment } = pendingAppointmentLink;
    const aptDate = new Date(appointment.startTime);
    setFormData((prev) => ({
      ...prev,
      appointmentId: appointment.id,
      treatmentDate: getIsraelDateString(aptDate),
      treatmentTime: aptDate.toLocaleTimeString("he-IL", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      treatmentTypeId: appointment.treatmentTypeId || prev.treatmentTypeId,
    }));
    setLinkingMode("select");
    setPendingAppointmentLink(null);
    setShowPendingLivePreview(false);
  };

  // Cancel: user declined — nothing is linked and nothing is overwritten.
  const handleCancelReplaceAndLink = () => {
    setPendingAppointmentLink(null);
    setShowPendingLivePreview(false);
  };

  // Switch to manual mode (no linking)
  const handleNoLinking = () => {
    setFormData((prev) => ({
      ...prev,
      appointmentId: null,
      treatmentTime: "", // Clear time - user must fill manually
    }));
    if (importedLiveSessionId) {
      clearImportedLiveSessionData();
    }
    setLinkingMode("manual");
  };

  // Add comment handler
  const handleAddComment = async () => {
    if (!newComment.trim() || !treatmentId || treatmentId === "new") return;

    try {
      await addComment({
        clinicId,
        treatmentId,
        content: newComment.trim(),
        authorId: therapistId,
        authorName: therapistName,
        patientId,
      }).unwrap();

      setNewComment("");
    } catch (error) {
      console.error("[CK-3302] Add comment failed:", {
        errorCode: "CK-3302",
        errorMessage: error.message,
        clinicId,
        treatmentId,
        timestamp: new Date().toISOString(),
      });
    }
  };

  // File handlers
  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFileUploadError(null);

    for (const file of selectedFiles) {
      // Check file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileUploadError(
          `הקובץ ${file.name} גדול מדי. הגודל המקסימלי הוא ${MAX_FILE_SIZE_MB}MB`,
        );
        continue;
      }

      // Add to pending files with default saveToPatientFolder = true
      setPendingFiles((prev) => [
        ...prev,
        {
          file,
          saveToPatientFolder: true, // Default: save to patient folder
          id: `${Date.now()}-${file.name}`,
        },
      ]);
    }

    // Reset all inputs
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const handleRemovePendingFile = (fileId) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleToggleSaveToFolder = (fileId) => {
    setPendingFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? { ...f, saveToPatientFolder: !f.saveToPatientFolder }
          : f,
      ),
    );
  };

  const uploadPendingFiles = async (savedTreatmentId) => {
    if (pendingFiles.length === 0) return;

    for (const pendingFile of pendingFiles) {
      try {
        await uploadFile({
          file: pendingFile.file,
          clinicId,
          patientId,
          therapistId,
          therapistName,
          treatmentId: savedTreatmentId,
          linkedToTreatmentOnly: !pendingFile.saveToPatientFolder,
        }).unwrap();
      } catch (error) {
        console.error("[CK-3310] Error uploading treatment file:", {
          errorCode: "CK-3310",
          errorMessage: error.message,
          fileName: pendingFile.file?.name,
          timestamp: new Date().toISOString(),
        });
      }
    }

    // Clear pending files after upload
    setPendingFiles([]);
  };

  const handleDeleteTreatmentFile = async (fileId) => {
    try {
      await deletePatientFile({
        clinicId,
        patientId,
        fileId,
        therapistId,
        therapistName,
      }).unwrap();
    } catch (error) {
      console.error("[CK-3311] Error deleting treatment file:", {
        errorCode: "CK-3311",
        errorMessage: error.message,
        fileId,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const getFileIcon = (fileType) => {
    const category = getFileTypeCategory(fileType);
    switch (category) {
      case "image":
        return Image;
      case "pdf":
        return FilePdf;
      case "word":
        return FileDoc;
      case "excel":
        return FileXls;
      default:
        return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Delete draft handler — only reachable for existing drafts (the button
  // is hidden for brand-new and locked treatments). Reuses the existing
  // RTK Query delete mutation and navigates back to the patient page.
  const handleDeleteDraft = async () => {
    if (!treatmentId || treatmentId === "new" || isNew) {
      setShowConfirmDelete(false);
      navigate(`/dashboard/patients/${patientId}?tab=treatments`);
      return;
    }

    try {
      await deleteTreatment({
        clinicId,
        treatmentId,
        patientId,
        therapistId,
        therapistName,
      }).unwrap();

      setShowConfirmDelete(false);
      navigate(`/dashboard/patients/${patientId}?tab=treatments`);
    } catch (error) {
      console.error("[CK-3303] Delete draft failed:", {
        errorCode: "CK-3303",
        errorMessage: error.message,
        clinicId,
        treatmentId,
        timestamp: new Date().toISOString(),
      });
    }
  };

  // Soft-delete a LOCKED report. The doc stays in Firestore (medical record)
  // but is flagged isDeleted so the system + AI ignore it. If the clinic has
  // the AI patient summary enabled we rebuild it server-side from the
  // remaining valid reports (the incremental summary can't be rolled back).
  const handleSoftDelete = async () => {
    if (!canSoftDelete) return;

    try {
      await softDeleteTreatment({
        clinicId,
        treatmentId,
        patientId,
        appointmentId: existingTreatment?.appointmentId || null,
        therapistId,
        therapistName,
        reason: softDeleteReason,
      }).unwrap();

      if (clinicProfile?.enableAiPatientSummary === true) {
        try {
          const rebuildResult = await rebuildPatientSummary({
            clinicId,
            patientId,
          });
          if (!rebuildResult?.success) {
            showError("הדוח נמחק, אך עדכון סיכום ה-AI של המטופל נכשל");
          }
        } catch (rebuildError) {
          console.error("[CK-3334] Patient summary rebuild after delete failed:", {
            errorCode: "CK-3334",
            errorMessage: rebuildError.message,
            clinicId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          showError("הדוח נמחק, אך עדכון סיכום ה-AI של המטופל נכשל");
        }
        await refetchPatient?.();
      }

      setSoftDeleteReason("");
      showSuccess("דוח הטיפול סומן כמחוק");
      navigate(`/dashboard/patients/${patientId}?tab=treatments`);
    } catch (error) {
      console.error("[CK-3334] Soft delete failed:", {
        errorCode: "CK-3334",
        errorMessage: error.message,
        clinicId,
        treatmentId,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "מחיקת הדוח נכשלה");
    }
  };

  // Navigate to treatment
  const handleTreatmentClick = (treatment) => {
    navigate(`/dashboard/patients/${patientId}/treatments/${treatment.id}`);
  };

  // Back navigation
  // ---- Header (tdh2) derived bits ----
  const isPersistedDraft = !isLocked && !isEditing && !isNew && treatmentId !== "new";
  const hasHeaderMenu = !isReadOnlyView && (canSoftDelete || isPersistedDraft);
  const headerTypeName = (() => {
    const typeId = formData.treatmentTypeId || existingTreatment?.treatmentTypeId;
    if (!typeId) return "";
    const type = treatmentTypes.find((t) => t.id === typeId);
    return type?.name || "";
  })();

  // Meta chips: date · treatment type · status badge · version. Rendered
  // inline under the title on desktop and as its own row on mobile.
  const renderHeaderMeta = () => (
    <>
      <span className="tdh2-chip">
        <CalendarBlank size={12} weight="regular" />
        {isNew
          ? "היום"
          : formatDate(existingTreatment?.treatmentDate || formData.treatmentDate)}
      </span>
      {headerTypeName && (
        <span className="tdh2-chip tdh2-chip-type" title={headerTypeName}>
          {headerTypeName}
        </span>
      )}
      {isDeleted ? (
        <span className="tdh2-status tdh2-status-deleted">
          <Prohibit size={11} weight="bold" />
          נמחק
        </span>
      ) : isEditing ? (
        <span className="tdh2-status tdh2-status-editing">
          <PencilSimple size={11} weight="bold" />
          בעריכה
        </span>
      ) : isLocked ? (
        <span className="tdh2-status tdh2-status-locked">
          <Lock size={11} weight="bold" />
          נעול
        </span>
      ) : (
        <span className="tdh2-status tdh2-status-draft">
          <LockOpen size={11} weight="bold" />
          {isNew ? "חדש" : "טיוטה"}
        </span>
      )}
      {isLocked && hasVersionHistory && !isDeleted && (
        <button
          type="button"
          className="tdh2-chip tdh2-chip-btn"
          onClick={() => setShowVersionHistory(true)}
          title="היסטוריית גרסאות"
        >
          <ClockCounterClockwise size={12} weight="bold" />
          גרסה {existingTreatment?.currentVersion || 1}
        </button>
      )}
    </>
  );

  const handleBack = () => {
    navigate(`/dashboard/patients/${patientId}?tab=treatments`);
  };

  // Get treatment type name
  const getTreatmentTypeName = (typeId) => {
    if (!typeId) return "פגישת טיפול";
    const type = treatmentTypes.find((t) => t.id === typeId);
    return type?.name || "פגישת טיפול";
  };

  // Get tag by ID
  const getTagById = (tagId) => {
    return treatmentTags.find((t) => t.id === tagId);
  };

  // Current template
  const currentTemplate =
    formData.templateId === "default"
      ? DEFAULT_TEMPLATE
      : templates.find((t) => t.id === formData.templateId);

  // Loading state. Skipped while the AI chat dialog is open: the first chat
  // message on a new report creates the draft and swaps the route to the
  // real id, which fires the treatment query — taking this early return then
  // would unmount the whole tree (dialog included) and make the popup flash
  // closed/open. With the chat open the form is already populated locally,
  // so rendering the main tree during that fetch is safe.
  if ((isLoadingPatient || isLoadingTreatment) && !showAiChat) {
    return (
      <div className="treatment-doc-page">
        <ContentLoader fill message="טוען..." />
      </div>
    );
  }

  // Permissions v2 gates — render a NoAccessState, never redirect silently
  if (canViewSensitive === false) {
    return (
      <div className="treatment-doc-page">
        <NoAccessState size="page" reason="הרשאה נדרשת: גישה למידע רפואי רגיש" />
      </div>
    );
  }
  if (isNew && canWriteTreatments === false && !isAdmin) {
    return (
      <div className="treatment-doc-page">
        <NoAccessState size="page" reason="הרשאה נדרשת: כתיבת דוחות טיפול" />
      </div>
    );
  }
  const readError = patientError || treatmentError;
  if (readError) {
    const errMsg = String(readError?.message || readError?.error || readError || "").toLowerCase();
    const isPermissionError =
      readError?.code === "permission-denied" ||
      errMsg.includes("permission") ||
      errMsg.includes("insufficient");
    if (isPermissionError) {
      return (
        <div className="treatment-doc-page">
          <NoAccessState size="page" message="המטופל/ת אינו/ה משויך/ת אליך יותר">
            <button
              className="back-btn"
              onClick={() => navigate("/dashboard/patients")}
            >
              חזרה לרשימת מטופלים
            </button>
          </NoAccessState>
        </div>
      );
    }
  }

  // Patient not found
  if (!patient) {
    return (
      <div className="treatment-doc-page">
        <div className="treatment-loading">
          <WarningCircle size={48} weight="duotone" />
          <span>מטופל לא נמצא</span>
          <button
            className="back-btn"
            onClick={() => navigate("/dashboard/patients")}
          >
            חזרה לרשימת מטופלים
          </button>
        </div>
      </div>
    );
  }

  // Timeline list body — rendered twice: inside the desktop sidebar and
  // inside the mobile bottom sheet. Closing the sheet on item click is a
  // no-op on desktop (the sheet is never open there).
  // Soft-deleted reports are not shown in the side timeline
  const visibleTreatments = treatments.filter((t) => t.isDeleted !== true);

  const timelineListContent = isLoadingTreatments ? (
    <ContentLoader size="sm" compact />
  ) : visibleTreatments.length === 0 ? (
    <div className="timeline-empty">
      <Stethoscope size={32} weight="duotone" />
      <p>תיעודי הטיפול יופיעו כאן לאחר השמירה הראשונה</p>
    </div>
  ) : (
    visibleTreatments.map((treatment) => (
      <div
        key={treatment.id}
        className={`timeline-item ${
          treatment.id === treatmentId ? "active" : ""
        } ${treatment.status === "draft" ? "draft" : ""}`}
        onClick={() => {
          setShowTimelineSheet(false);
          handleTreatmentClick(treatment);
        }}
      >
        <div className="timeline-dot" />
        <div className="timeline-item-content">
          <div className="timeline-item-date">
            {formatDate(treatment.treatmentDate)}{" "}
            {formatTime(treatment.treatmentDate)}
          </div>
          <div className="timeline-item-type">
            {getTreatmentTypeName(treatment.treatmentTypeId)}
            {treatment.status === "draft" && " (טיוטה)"}
          </div>
          <div className="timeline-item-summary">{treatment.summary}</div>
          {treatment.tagIds?.length > 0 && (
            <div className="timeline-item-tags">
              {treatment.tagIds.slice(0, 2).map((tagId) => {
                const tag = getTagById(tagId);
                return tag ? (
                  <span
                    key={tagId}
                    className="timeline-tag"
                    style={{
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                    }}
                  >
                    {tag.name}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      </div>
    ))
  );

  // Sticky mobile save bar visibility — also gates the extra bottom padding
  // the form needs so content isn't hidden behind the fixed bar.
  const showMobileSavebar =
    !showSetupWizard && !isReadOnlyView && !(isLocked && !isEditing);

  return (
    <div
      className={`treatment-doc-page ${
        showMobileSavebar ? "has-mobile-savebar" : ""
      }`}
    >
      {aiProcessingStatus === "processing" && (
        <GlobalLoader message="שומר ומעבד סיכומים..." />
      )}

      {/* Header — 3-zone grid: back (start) / patient identity (center) /
          actions (end). Desktop shows the full action set inline; mobile
          collapses to a timeline button + overflow (⋯) menu, with the save
          CTA living in the sticky bottom bar instead. */}
      {/* Page header — 2026 language: flat card strip, square avatar tile,
          eyebrow + title + meta chips, contextual actions. Consequential
          actions (delete) live in the ⋯ dropdown; edit / save are explicit
          buttons. ≤768px: row 1 = back · identity · ⋯, row 2 = meta · tools. */}
      <header className={`tdh2 ${isDeleted ? "is-deleted" : isEditing ? "is-editing" : isLocked ? "is-locked" : "is-draft"}`}>
        <div className="tdh2-row tdh2-main">
          <button
            type="button"
            className="tdh2-icon-btn"
            onClick={handleBack}
            aria-label="חזרה"
            title="חזרה"
          >
            <ArrowRight size={18} weight="bold" />
          </button>

          <div className="tdh2-identity">
            <div className="tdh2-avatar" aria-hidden="true">
              {patient.firstName?.charAt(0)}
              {patient.lastName?.charAt(0)}
            </div>
            <div className="tdh2-text">
              <span className="tdh2-eyebrow">
                {isNew
                  ? "תיעוד חדש"
                  : isEditing
                    ? "מצב עריכה · הגרסה הקודמת תישמר"
                    : "סיכום טיפול"}
              </span>
              <h2 className="tdh2-title">
                {patient.firstName} {patient.lastName}
              </h2>
              <div className="tdh2-meta tdh2-meta-desktop">
                {renderHeaderMeta()}
              </div>
            </div>
          </div>

          <div className="tdh2-actions">
            <span className="tdh2-desktop-only tdh2-help">
              <HelpButton featureKey="treatment_doc" />
            </span>

            {canExportPdf && (
              <button
                type="button"
                className="tdh2-btn tdh2-btn-secondary tdh2-desktop-only"
                onClick={() => navigate(exportPath)}
              >
                <FilePdf size={16} weight="bold" />
                ייצוא PDF
              </button>
            )}

            {isDeleted || isReadOnlyView || (isLocked && !isEditing && !canEditLocked) ? null : isLocked && !isEditing ? (
              <button
                type="button"
                className="tdh2-btn tdh2-btn-secondary tdh2-desktop-only"
                onClick={() => setShowConfirmEdit(true)}
              >
                <PencilSimple size={16} weight="bold" />
                עריכה
              </button>
            ) : isEditing ? (
              <>
                <button
                  type="button"
                  className="tdh2-btn tdh2-btn-secondary tdh2-desktop-only"
                  onClick={handleCancelEdit}
                >
                  <X size={16} weight="bold" />
                  ביטול
                </button>
                <button
                  type="button"
                  className="tdh2-btn tdh2-btn-primary tdh2-desktop-only"
                  onClick={handleSaveLockedEdit}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <LoaderLoop size="xs" />
                  ) : (
                    <FloppyDisk size={16} weight="bold" />
                  )}
                  שמור שינויים
                </button>
              </>
            ) : (
              <>
                {autoSaveStatus && (
                  <span
                    className={`tdh2-autosave tdh2-desktop-only ${autoSaveStatus}`}
                  >
                    {autoSaveStatus === "saving" ? (
                      <>
                        <LoaderLoop size="xs" />
                        שומר...
                      </>
                    ) : (
                      <>
                        <Check size={13} weight="bold" />
                        נשמר
                      </>
                    )}
                  </span>
                )}

                {/* Single primary CTA — opens the unified summary popup. For
                    brand-new treatments we silently create the draft first so
                    the popup has a real `treatmentId` for AI + Lock & Save. */}
                <span className="tdh2-cta-wrap tdh2-desktop-only">
                  <button
                    type="button"
                    className="tdh2-btn tdh2-btn-primary"
                    onClick={handleOpenSummary}
                    disabled={
                      isSaving || isCreatingDraft || !formData.treatmentTime
                    }
                  >
                    {isCreatingDraft ? (
                      <LoaderLoop size="xs" />
                    ) : (
                      <FloppyDisk size={16} weight="bold" />
                    )}
                    {isCreatingDraft ? "יוצר טיוטה..." : "שמור סיכום טיפול"}
                  </button>
                  {!formData.treatmentTime && !isSaving && (
                    <span className="tdh2-cta-hint">
                      <WarningCircle size={11} weight="bold" />
                      יש לבחור שעת טיפול
                    </span>
                  )}
                </span>
              </>
            )}

            {hasHeaderMenu && (
              <div className="tdh2-menu-wrap">
                <button
                  type="button"
                  className="tdh2-icon-btn"
                  onClick={() => setShowHeaderMenu((prev) => !prev)}
                  aria-label="פעולות נוספות"
                  aria-haspopup="menu"
                  aria-expanded={showHeaderMenu}
                >
                  <DotsThreeVertical size={18} weight="bold" />
                </button>
                {showHeaderMenu && (
                  <>
                    <div
                      className="tdh2-menu-backdrop"
                      onClick={() => setShowHeaderMenu(false)}
                    />
                    <div className="tdh2-menu" role="menu">
                      {canSoftDelete && (
                        <button
                          type="button"
                          role="menuitem"
                          className="tdh2-menu-danger"
                          onClick={() => {
                            setShowHeaderMenu(false);
                            setShowConfirmSoftDelete(true);
                          }}
                          disabled={isSoftDeleting}
                        >
                          <Trash size={16} weight="bold" />
                          מחיקת דוח
                        </button>
                      )}
                      {isPersistedDraft && (
                        <button
                          type="button"
                          role="menuitem"
                          className="tdh2-menu-danger"
                          onClick={() => {
                            setShowHeaderMenu(false);
                            setShowConfirmDelete(true);
                          }}
                          disabled={isSaving || isDeleting || isCreatingDraft}
                        >
                          <Trash size={16} weight="bold" />
                          מחיקת טיוטא
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile-only second row: meta chips + tools (help / timeline / edit).
            Save & edit-save live in the sticky bottom bar. */}
        <div className="tdh2-row tdh2-mobile-row">
          <div className="tdh2-meta">{renderHeaderMeta()}</div>
          <div className="tdh2-mobile-tools">
            <span className="tdh2-help">
              <HelpButton featureKey="treatment_doc" />
            </span>
            <button
              type="button"
              className="tdh2-icon-btn"
              onClick={() => setShowTimelineSheet(true)}
              aria-label="ציר זמן"
              title="ציר זמן"
            >
              <ClockCounterClockwise size={18} weight="bold" />
            </button>
            {canExportPdf && (
              <button
                type="button"
                className="tdh2-icon-btn"
                onClick={() => navigate(exportPath)}
                aria-label="ייצוא PDF"
                title="ייצוא PDF"
              >
                <FilePdf size={18} weight="bold" />
              </button>
            )}
            {canEditLocked && (
              <button
                type="button"
                className="tdh2-btn tdh2-btn-secondary tdh2-btn-compact"
                onClick={() => setShowConfirmEdit(true)}
              >
                <PencilSimple size={15} weight="bold" />
                עריכה
              </button>
            )}
          </div>
        </div>
      </header>

      {isReadOnlyView && (
        <ReadOnlyBanner text="תצוגה בלבד — הדוח נכתב על ידי מטפל/ת אחר/ת." />
      )}

      {/* Main Content */}
      <div className="treatment-content">
        {/* Form Area */}
        <div className="treatment-form-area">
          <div className="treatment-form-scroll">
            <div
              className={`treatment-form ${isFieldsDisabled ? "locked-treatment" : ""} ${isEditing ? "editing-locked-treatment" : ""}`}
            >
              {/* Deleted Banner — soft-deleted report, read-only, no restore */}
              {isDeleted && (
                <div className="locked-banner deleted-banner" role="status">
                  <Prohibit size={20} weight="bold" />
                  <div className="deleted-banner-text">
                    <span>דוח זה נמחק – אינו תקף</span>
                    <small>
                      {existingTreatment?.deletedAt
                        ? `נמחק ב-${formatDateTime(existingTreatment.deletedAt)}`
                        : "נמחק"}
                      {existingTreatment?.deletedByName
                        ? ` על ידי ${existingTreatment.deletedByName}`
                        : ""}
                      {existingTreatment?.deletedReason
                        ? ` · סיבה: ${existingTreatment.deletedReason}`
                        : ""}
                    </small>
                  </div>
                </div>
              )}

              {/* Editing Locked Banner */}
              {isEditing && (
                <div className="locked-banner editing-banner">
                  <PencilSimple size={20} weight="bold" />
                  <span>מצב עריכה - הגרסה הקודמת תישמר</span>
                </div>
              )}

              {/* AI Summary Box - Only show when locked (not editing) and has summary */}
              {isLocked && !isEditing && existingTreatment?.summary && (
                <div className="ai-summary-box">
                  <div className="ai-summary-header">
                    <Sparkle
                      size={18}
                      weight="fill"
                      className="ai-summary-icon"
                    />
                    <span className="ai-summary-label">
                      סיכום פגישה שנוצר ע״י AI
                    </span>
                  </div>
                  <div className="ai-summary-content">
                    {existingTreatment.summary}
                  </div>
                </div>
              )}

              {/* Guided setup for new treatments: 1) link appointment,
                  2) pick template. Once done, the compact details card +
                  full form render instead. */}
              {showSetupWizard && (
                <div className="treatment-setup-card">
                  <div className="treatment-setup-header">
                    <div className="treatment-setup-steps">
                      <button
                        type="button"
                        className={`treatment-setup-step ${
                          setupStep === 1 ? "active" : "done"
                        }`}
                        disabled={isAppointmentLocked}
                        onClick={() => setSetupStep(1)}
                      >
                        <span className="treatment-setup-step-dot">
                          {setupStep > 1 || hasCompletedSetup ? (
                            <Check size={13} weight="bold" />
                          ) : (
                            "1"
                          )}
                        </span>
                        שיוך לפגישה
                      </button>
                      <span className="treatment-setup-step-line" />
                      <button
                        type="button"
                        className={`treatment-setup-step ${
                          setupStep === 2
                            ? "active"
                            : hasCompletedSetup
                              ? "done"
                              : ""
                        }`}
                        disabled={setupStep < 2 && !hasCompletedSetup}
                        onClick={() => setSetupStep(2)}
                      >
                        <span className="treatment-setup-step-dot">
                          {hasCompletedSetup && setupStep !== 2 ? (
                            <Check size={13} weight="bold" />
                          ) : (
                            "2"
                          )}
                        </span>
                        בחירת תבנית
                      </button>
                      <span className="treatment-setup-step-line" />
                      {hasCompletedSetup ? (
                        <button
                          type="button"
                          className="treatment-setup-step"
                          onClick={() => setSetupStep(3)}
                        >
                          <span className="treatment-setup-step-dot">3</span>
                          תיעוד הטיפול
                        </button>
                      ) : (
                        <span className="treatment-setup-step upcoming">
                          <span className="treatment-setup-step-dot">3</span>
                          תיעוד הטיפול
                        </span>
                      )}
                    </div>

                    {hasCompletedSetup && (
                      <button
                        type="button"
                        className="treatment-setup-close"
                        onClick={() => {
                          setPendingTemplateChange(null);
                          setSetupStep(3);
                        }}
                      >
                        <X size={14} weight="bold" />
                        חזרה לתיעוד
                      </button>
                    )}
                  </div>

                  {setupStep === 1 && (
                    <div className="treatment-setup-body" key="step-1">
                      <h4 className="treatment-setup-title">
                        לאיזו פגישה שייך התיעוד?
                      </h4>
                      <p className="treatment-setup-subtitle">
                        בחירת פגישה תמלא אוטומטית את התאריך, השעה וסוג הטיפול
                      </p>

                      <div className="treatment-setup-apt-list">
                        {displayedAppointments.map((apt) => {
                          const isSelected = formData.appointmentId === apt.id;
                          return (
                            <button
                              key={apt.id}
                              type="button"
                              className={`treatment-setup-apt-row ${
                                isSelected ? "selected" : ""
                              }`}
                              onClick={() => {
                                if (isSelected) {
                                  setSetupStep(hasCompletedSetup ? 3 : 2);
                                } else {
                                  handleAppointmentSelect(apt);
                                }
                              }}
                            >
                              <span className="treatment-setup-apt-icon">
                                {isSelected ? (
                                  <Check size={16} weight="bold" />
                                ) : (
                                  <CalendarBlank size={16} weight="regular" />
                                )}
                              </span>
                              <span className="treatment-setup-apt-main">
                                <strong>{formatDate(apt.startTime)}</strong>
                                <span>
                                  {formatTime(apt.startTime)} ·{" "}
                                  {getTreatmentTypeName(apt.treatmentTypeId)}
                                </span>
                              </span>
                              <ArrowLeft
                                size={16}
                                weight="bold"
                                className="treatment-setup-apt-go"
                              />
                            </button>
                          );
                        })}

                        {displayedAppointments.length === 0 && (
                          <div className="treatment-setup-empty">
                            <CalendarBlank size={28} weight="duotone" />
                            <span>אין פגישות עבר ללא תיעוד</span>
                          </div>
                        )}

                        {/* Manual entry — styled as a row so it reads as an
                            equal choice, not an afterthought */}
                        <button
                          type="button"
                          className={`treatment-setup-apt-row manual ${
                            !formData.appointmentId && linkingMode === "manual"
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              !formData.appointmentId &&
                              linkingMode === "manual"
                            ) {
                              setSetupStep(hasCompletedSetup ? 3 : 2);
                              return;
                            }
                            handleNoLinking();
                            setSetupStep(hasCompletedSetup ? 3 : 2);
                          }}
                        >
                          <span className="treatment-setup-apt-icon">
                            {!formData.appointmentId &&
                            linkingMode === "manual" ? (
                              <Check size={16} weight="bold" />
                            ) : (
                              <LinkBreak size={16} weight="regular" />
                            )}
                          </span>
                          <span className="treatment-setup-apt-main">
                            <strong>ללא שיוך לפגישה</strong>
                            <span>הזנת תאריך ושעה באופן ידני</span>
                          </span>
                          <ArrowLeft
                            size={16}
                            weight="bold"
                            className="treatment-setup-apt-go"
                          />
                        </button>
                      </div>
                    </div>
                  )}

                  {setupStep === 2 && (
                    <div className="treatment-setup-body" key="step-2">
                      <h4 className="treatment-setup-title">
                        באיזו תבנית להשתמש?
                      </h4>
                      <p className="treatment-setup-subtitle">
                        {hasCompletedSetup
                          ? "בחירת תבנית אחרת תאפס את השדות שכבר מולאו"
                          : "התבנית קובעת אילו שדות יופיעו בתיעוד הטיפול"}
                      </p>

                      <div className="treatment-setup-tpl-grid">
                        {[DEFAULT_TEMPLATE, ...templates].map((template) => {
                          const isSelected =
                            formData.templateId === template.id;
                          const fieldCount = template.fields?.length || 0;
                          return (
                            <button
                              key={template.id}
                              type="button"
                              className={`treatment-setup-tpl-card ${
                                isSelected ? "selected" : ""
                              }`}
                              onClick={() => pickTemplate(template)}
                            >
                              {isSelected && (
                                <span className="treatment-setup-tpl-badge">
                                  <Check size={11} weight="bold" />
                                </span>
                              )}
                              <span className="treatment-setup-tpl-icon">
                                <FileText size={20} weight="duotone" />
                              </span>
                              <span className="treatment-setup-tpl-name">
                                {template.name}
                              </span>
                              <span className="treatment-setup-tpl-meta">
                                {fieldCount === 1
                                  ? "שדה אחד"
                                  : `${fieldCount} שדות`}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {pendingTemplateChange && (
                        <div className="treatment-setup-confirm">
                          <WarningCircle size={20} weight="fill" />
                          <div className="treatment-setup-confirm-text">
                            <strong>
                              להחליף לתבנית "{pendingTemplateChange.name}"?
                            </strong>
                            <span>
                              החלפת התבנית תאפס את התוכן שכבר מילאת בשדות
                            </span>
                          </div>
                          <div className="treatment-setup-confirm-actions">
                            <button
                              type="button"
                              className="treatment-setup-confirm-replace"
                              onClick={() => {
                                handleTemplateSelect(pendingTemplateChange);
                                setPendingTemplateChange(null);
                                setSetupStep(3);
                                setHasCompletedSetup(true);
                              }}
                            >
                              החלף תבנית
                            </button>
                            <button
                              type="button"
                              className="treatment-setup-confirm-cancel"
                              onClick={() => setPendingTemplateChange(null)}
                            >
                              ביטול
                            </button>
                          </div>
                        </div>
                      )}

                      {!isAppointmentLocked && (
                        <button
                          type="button"
                          className="treatment-setup-back"
                          onClick={() => {
                            setPendingTemplateChange(null);
                            setSetupStep(1);
                          }}
                        >
                          <ArrowRight size={14} weight="bold" />
                          חזרה לבחירת פגישה
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Setup overview — two cards showing the linked meeting and
                  chosen template, each with a gentle "change" action */}
              {!showSetupWizard && isNew && !isLocked && (
                <div className="treatment-setup-overview">
                  <div className="treatment-overview-card">
                    <div className="treatment-overview-head">
                      <span className="treatment-overview-icon">
                        {formData.appointmentId ? (
                          <LinkSimple size={18} weight="duotone" />
                        ) : (
                          <LinkBreak size={18} weight="duotone" />
                        )}
                      </span>
                      <span className="treatment-overview-label">
                        פגישה משויכת
                      </span>
                    </div>
                    <div className="treatment-overview-value">
                      {linkedAppointment ? (
                        <>
                          <strong>
                            {formatDate(linkedAppointment.startTime)} ·{" "}
                            {formatTime(linkedAppointment.startTime)}
                          </strong>
                          <span>
                            {getTreatmentTypeName(
                              linkedAppointment.treatmentTypeId,
                            )}
                          </span>
                        </>
                      ) : formData.appointmentId ? (
                        <strong>משויך לפגישה</strong>
                      ) : (
                        <>
                          <strong>ללא שיוך לפגישה</strong>
                          <span>התאריך והשעה הוזנו ידנית</span>
                        </>
                      )}
                    </div>
                    {isAppointmentLocked && (
                      <p className="treatment-overview-locked-note">
                        הדוח נפתח עבור פגישה זו ולכן לא ניתן לשנות את השיוך
                      </p>
                    )}
                    <button
                      type="button"
                      className="treatment-overview-btn"
                      disabled={isAppointmentLocked}
                      onClick={() => setSetupStep(1)}
                    >
                      <PencilSimple size={13} weight="regular" />
                      שיוך פגישה אחרת לדוח
                    </button>
                  </div>

                  <div className="treatment-overview-card">
                    <div className="treatment-overview-head">
                      <span className="treatment-overview-icon">
                        <FileText size={18} weight="duotone" />
                      </span>
                      <span className="treatment-overview-label">
                        תבנית התיעוד
                      </span>
                    </div>
                    <div className="treatment-overview-value">
                      <strong>{currentTemplate?.name || "תבנית בסיסית"}</strong>
                      <span>
                        {(currentTemplate?.fields?.length || 0) === 1
                          ? "שדה אחד"
                          : `${currentTemplate?.fields?.length || 0} שדות`}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="treatment-overview-btn"
                      onClick={() => setSetupStep(2)}
                    >
                      <PencilSimple size={13} weight="regular" />
                      בחירת תבנית אחרת
                    </button>
                  </div>
                </div>
              )}

              {/* Treatment Details — compact card */}
              {!showSetupWizard && (
                <div
                  className={`treatment-form-section treatment-details-card ${
                    missingRequired.details ? "section-has-missing" : ""
                  }`}
                >
                  <div className="treatment-form-section-header">
                    <h3>
                      <Stethoscope size={20} weight="duotone" />
                      פרטי הטיפול
                    </h3>
                    {patient && (
                      <div className="treatment-patient-line">
                        <User size={14} weight="regular" />
                        {patient.firstName} {patient.lastName}
                      </div>
                    )}
                  </div>

                  {/* Date/time/type entry */}
                  {(linkingMode === "manual" ||
                    !isNew ||
                    formData.appointmentId) && (
                    <div className="treatment-form-row">
                      <div className="treatment-form-group">
                        <label>תאריך הטיפול</label>
                        <DatePicker
                          value={formData.treatmentDate}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              treatmentDate: e.target.value,
                            }))
                          }
                          disabled={
                            isFieldsDisabled ||
                            (formData.appointmentId && isNew)
                          }
                        />
                      </div>

                      <div className="treatment-form-group">
                        <label className="required">שעת הטיפול</label>
                        <TimePicker
                          value={formData.treatmentTime}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              treatmentTime: e.target.value,
                            }))
                          }
                          disabled={
                            isFieldsDisabled ||
                            (formData.appointmentId && isNew)
                          }
                          error={!formData.treatmentTime && !isFieldsDisabled}
                        />
                        {!formData.treatmentTime && !isFieldsDisabled && (
                          <span className="treatment-field-hint-error">
                            יש להזין שעת טיפול
                          </span>
                        )}
                      </div>

                      <div className="treatment-form-group">
                        <label>סוג טיפול</label>
                        <select
                          value={formData.treatmentTypeId}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              treatmentTypeId: e.target.value,
                            }))
                          }
                          disabled={
                            isFieldsDisabled ||
                            (formData.appointmentId && isNew)
                          }
                        >
                          <option value="">פגישת טיפול</option>
                          {treatmentTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {treatmentTags.length > 0 && (
                    <div className="treatment-form-row">
                      <div className="treatment-form-group treatment-form-group-full">
                        <label>
                          <Tag size={14} weight="regular" /> תגיות
                        </label>
                        <div className="treatment-tags-selection">
                          {treatmentTags.map((tag) => (
                            <button
                              key={tag.id}
                              type="button"
                              className={`treatment-tag-option ${
                                formData.tagIds.includes(tag.id)
                                  ? "selected"
                                  : ""
                              }`}
                              style={{
                                backgroundColor: `${tag.color}20`,
                                color: tag.color,
                              }}
                              onClick={() => handleTagToggle(tag.id)}
                              disabled={isFieldsDisabled}
                            >
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Template Fields */}
              {!showSetupWizard && currentTemplate && (
                <div
                  className={`treatment-form-section treatment-report-card ${
                    missingRequired.templateFieldIds.length > 0
                      ? "section-has-missing"
                      : ""
                  }`}
                >
                  <div className="treatment-form-section-header">
                    <h3>
                      <FileText size={20} weight="duotone" />
                      {currentTemplate.name}
                    </h3>
                  </div>

                  <div className="treatment-template-fields">
                    {formData.fields.map((field) => {
                      if (field.type === "section_header") {
                        return (
                          <div
                            key={field.id}
                            className="treatment-template-section-header"
                            data-treatment-field-id={field.id}
                          >
                            {field.label}
                          </div>
                        );
                      }
                      return (
                      <div
                        key={field.id}
                        className={`treatment-template-field ${
                          missingRequired.templateFieldIds.includes(field.id)
                            ? "field-missing"
                            : ""
                        }`}
                        data-treatment-field-id={field.id}
                      >
                        <label className={field.required ? "required" : ""}>
                          {field.label}
                        </label>

                        {field.type === "checkbox" ? (
                          <label className="treatment-checkbox-label">
                            <input
                              type="checkbox"
                              checked={
                                field.value === true || field.value === "true"
                              }
                              onChange={(e) =>
                                handleFieldChange(field.id, e.target.checked)
                              }
                              disabled={isFieldsDisabled}
                            />
                            <span>{field.label}</span>
                          </label>
                        ) : field.type === "date" ? (
                          <DatePicker
                            value={field.value || ""}
                            onChange={(e) =>
                              handleFieldChange(field.id, e.target.value)
                            }
                            disabled={isFieldsDisabled}
                            className="treatment-date-input"
                            error={missingRequired.templateFieldIds.includes(
                              field.id,
                            )}
                          />
                        ) : field.type === "long_text" ? (
                          <RichTextEditor
                            value={field.value || ""}
                            onChange={(value) =>
                              handleFieldChange(field.id, value)
                            }
                            placeholder={
                              field.placeholder || `הכנס ${field.label}...`
                            }
                            disabled={isFieldsDisabled}
                            size="long"
                          />
                        ) : field.type === "select" ? (
                          <select
                            value={field.value || ""}
                            onChange={(e) =>
                              handleFieldChange(field.id, e.target.value)
                            }
                            disabled={isFieldsDisabled}
                            className="treatment-text-input"
                            dir="rtl"
                          >
                            <option value="">בחר תשובה...</option>
                            {(field.options || []).map((option, optIdx) => (
                              <option key={optIdx} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : field.type === "radio" ? (
                          <div className="treatment-radio-options">
                            {(field.options || []).map((option, optIdx) => (
                              <label key={optIdx} className="treatment-radio">
                                <input
                                  type="radio"
                                  name={`field-${field.id}`}
                                  checked={field.value === option}
                                  onChange={() =>
                                    handleFieldChange(field.id, option)
                                  }
                                  disabled={isFieldsDisabled}
                                />
                                <span>{option}</span>
                              </label>
                            ))}
                          </div>
                        ) : field.type === "multi_select" ? (
                          <div className="treatment-radio-options">
                            {(field.options || []).map((option, optIdx) => (
                              <label key={optIdx} className="treatment-radio">
                                <input
                                  type="checkbox"
                                  checked={
                                    Array.isArray(field.value) &&
                                    field.value.includes(option)
                                  }
                                  onChange={() =>
                                    handleMultiSelectToggle(field.id, option)
                                  }
                                  disabled={isFieldsDisabled}
                                />
                                <span>{option}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={field.value || ""}
                            onChange={(e) =>
                              handleFieldChange(field.id, e.target.value)
                            }
                            placeholder={
                              field.placeholder || `הכנס ${field.label}...`
                            }
                            disabled={isFieldsDisabled}
                            className="treatment-text-input"
                          />
                        )}
                        {missingRequired.templateFieldIds.includes(
                          field.id,
                        ) && (
                          <span className="treatment-field-hint-error">
                            שדה חובה — יש למלא
                          </span>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* AI consult card — sits below the rendered template fields */}
              {!showSetupWizard && currentTemplate && !isFieldsDisabled && (
                <div className="treatment-overview-card treatment-ai-card">
                  <div className="treatment-overview-head">
                    <span className="treatment-overview-icon">
                      <Sparkle size={18} weight="duotone" />
                    </span>
                    <span className="treatment-overview-label">
                      התייעצות עם AI
                    </span>
                  </div>
                  <p className="treatment-ai-card-desc">
                    אפשר לשאול את ה-AI שאלות על דוח הטיפול הזה. ה-AI מכיר את
                    כל מה שמולא בטופס, וגם את סוג הקליניקה, גיל ומגדר המטופל —
                    הוא לא יודע שום פרט אישי עליך או על המטופל.
                  </p>
                  <button
                    type="button"
                    className="treatment-overview-btn"
                    onClick={() => setShowAiChat(true)}
                  >
                    <Sparkle size={13} weight="fill" />
                    התייעצות עם AI
                  </button>
                  <p className="treatment-ai-card-warning">
                    שימו לב: ה-AI עונה בביטחון מלא גם כשהוא טועה — אין להסתמך
                    עליו בנושאים רגישים.
                  </p>
                </div>
              )}

              {/* Game Selection Section - Speech Therapist Only */}
              {!showSetupWizard &&
                isSpeechTherapist &&
                clinicGames.length > 0 && (
                  <div className="game-selection-section">
                    <h3>
                      <GameController size={20} weight="duotone" />
                      בחירת משחק
                    </h3>
                    <p className="section-description">
                      בחר את המשחקים שהשתמשת בהם בטיפול זה
                    </p>

                    <div className="games-grid">
                      {clinicGames.map((game) => {
                        const isSelected = (
                          formData.selectedGameIds || []
                        ).includes(game.id);
                        const gameGoals = getGoalsByIds(game.goalIds || []);
                        const ageRange =
                          game.minAge || game.maxAge
                            ? `${game.minAge || "?"}-${game.maxAge || "?"}`
                            : null;

                        return (
                          <button
                            key={game.id}
                            type="button"
                            className={`game-card ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() => {
                              if (isFieldsDisabled) return;
                              setFormData((prev) => ({
                                ...prev,
                                selectedGameIds: isSelected
                                  ? (prev.selectedGameIds || []).filter(
                                      (id) => id !== game.id,
                                    )
                                  : [...(prev.selectedGameIds || []), game.id],
                              }));
                            }}
                            disabled={isFieldsDisabled}
                          >
                            <div className="game-card-header">
                              <span className="game-card-name">
                                {game.name}
                              </span>
                              {isSelected && (
                                <span className="game-selected-check">
                                  <Check size={14} weight="bold" />
                                </span>
                              )}
                            </div>
                            {ageRange && (
                              <span className="game-card-age">
                                גילאים {ageRange}
                              </span>
                            )}
                            {gameGoals.length > 0 && (
                              <div className="game-card-goals">
                                {gameGoals.slice(0, 2).map((goal) => (
                                  <span
                                    key={goal.id}
                                    className="game-goal-tag"
                                    style={{
                                      backgroundColor:
                                        goal.categoryColor + "15",
                                      color: goal.categoryColor,
                                    }}
                                  >
                                    {goal.name}
                                  </span>
                                ))}
                                {gameGoals.length > 2 && (
                                  <span className="game-goals-more">
                                    +{gameGoals.length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {(formData.selectedGameIds || []).length > 0 && (
                      <div className="selected-games-summary">
                        נבחרו {(formData.selectedGameIds || []).length} משחקים
                      </div>
                    )}
                  </div>
                )}

              {/* Files Section */}
              {!showSetupWizard && (
                <div className="treatment-form-section">
                  <div
                    className="treatment-form-section-header clickable"
                    onClick={() => setShowFilesSection(!showFilesSection)}
                  >
                    <h3>
                      <Paperclip size={20} weight="duotone" />
                      קבצים מצורפים
                      {(treatmentFiles.length > 0 ||
                        pendingFiles.length > 0) && (
                        <span className="files-badge">
                          {treatmentFiles.length + pendingFiles.length}
                        </span>
                      )}
                    </h3>
                    <span
                      className={`section-toggle ${
                        showFilesSection ? "open" : ""
                      }`}
                    >
                      {showFilesSection ? "הסתר" : "הצג"}
                    </span>
                  </div>

                  {showFilesSection && (
                    <div className="treatment-files-content">
                      {/* Upload Buttons */}
                      {!isFieldsDisabled && (
                        <div className="treatment-files-upload">
                          <div className="treatment-upload-buttons">
                            {/* Camera Button - Mobile only */}
                            {isMobile && (
                              <button
                                type="button"
                                className="treatment-upload-btn camera-btn"
                                onClick={() => cameraInputRef.current?.click()}
                                disabled={isUploadingFile}
                              >
                                {isUploadingFile ? (
                                  <LoaderLoop size="xs" />
                                ) : (
                                  <Camera size={16} weight="bold" />
                                )}
                                צלם
                              </button>
                            )}

                            {/* Gallery Button - Mobile only */}
                            {isMobile && (
                              <button
                                type="button"
                                className="treatment-upload-btn gallery-btn"
                                onClick={() => galleryInputRef.current?.click()}
                                disabled={isUploadingFile}
                              >
                                {isUploadingFile ? (
                                  <LoaderLoop size="xs" />
                                ) : (
                                  <ImageSquare size={16} weight="bold" />
                                )}
                                גלריה
                              </button>
                            )}

                            {/* Regular File Upload Button */}
                            <button
                              type="button"
                              className="treatment-upload-btn"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isUploadingFile}
                            >
                              {isUploadingFile ? (
                                <LoaderLoop size="xs" />
                              ) : (
                                <Plus size={16} weight="bold" />
                              )}
                              {isMobile ? "קובץ" : "הוסף קובץ"}
                            </button>
                          </div>

                          {/* Hidden file inputs */}
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            onChange={handleFileSelect}
                            style={{ display: "none" }}
                          />
                          <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleFileSelect}
                            style={{ display: "none" }}
                          />
                          <input
                            ref={galleryInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleFileSelect}
                            style={{ display: "none" }}
                          />

                          <span className="upload-hint">
                            מקסימום {MAX_FILE_SIZE_MB}MB לקובץ
                          </span>
                        </div>
                      )}

                      {/* Error Message */}
                      {fileUploadError && (
                        <div className="treatment-files-error">
                          <span>{fileUploadError}</span>
                          <button onClick={() => setFileUploadError(null)}>
                            <X size={14} weight="bold" />
                          </button>
                        </div>
                      )}

                      {/* Pending Files (before save) */}
                      {pendingFiles.length > 0 && (
                        <div className="treatment-pending-files">
                          <div className="pending-files-header">
                            <span>קבצים להעלאה</span>
                          </div>
                          {pendingFiles.map((pf) => {
                            const FileIcon = getFileIcon(pf.file.type);
                            return (
                              <div
                                key={pf.id}
                                className="treatment-file-item pending"
                              >
                                <div className="file-item-icon">
                                  <FileIcon size={20} weight="duotone" />
                                </div>
                                <div className="file-item-info">
                                  <span className="file-item-name">
                                    {pf.file.name}
                                  </span>
                                  <span className="file-item-size">
                                    {formatFileSize(pf.file.size)}
                                  </span>
                                </div>
                                <div className="file-item-toggle">
                                  <label className="toggle-label">
                                    <input
                                      type="checkbox"
                                      checked={pf.saveToPatientFolder}
                                      onChange={() =>
                                        handleToggleSaveToFolder(pf.id)
                                      }
                                    />
                                    <span className="toggle-text">
                                      {pf.saveToPatientFolder ? (
                                        <>
                                          <Folder size={14} weight="fill" />
                                          שמור בתיק
                                        </>
                                      ) : (
                                        "צרף לפגישה בלבד"
                                      )}
                                    </span>
                                  </label>
                                </div>
                                <button
                                  type="button"
                                  className="file-item-remove"
                                  onClick={() => handleRemovePendingFile(pf.id)}
                                >
                                  <X size={16} weight="bold" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Existing Files (saved) */}
                      {treatmentFiles.length > 0 && (
                        <div className="treatment-saved-files">
                          {!isNew && (
                            <div className="saved-files-header">
                              <span>קבצים מצורפים</span>
                            </div>
                          )}
                          {treatmentFiles.map((file) => {
                            const FileIcon = getFileIcon(file.fileType);
                            return (
                              <div
                                key={file.id}
                                className="treatment-file-item saved"
                              >
                                <div className="file-item-icon">
                                  <FileIcon size={20} weight="duotone" />
                                </div>
                                <div className="file-item-info">
                                  <span className="file-item-name">
                                    {file.fileName}
                                  </span>
                                  <div className="file-item-meta">
                                    <span>{formatFileSize(file.fileSize)}</span>
                                    {!file.linkedToTreatmentOnly && (
                                      <span className="file-in-folder">
                                        <Folder size={12} weight="fill" />
                                        בתיק המטופל
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="file-item-actions">
                                  {isFilePreviewable(file.fileType) && (
                                    <a
                                      href={file.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="file-action-btn"
                                      title="צפייה"
                                    >
                                      <Eye size={16} weight="bold" />
                                    </a>
                                  )}
                                  <a
                                    href={file.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="file-action-btn"
                                    title="הורדה"
                                  >
                                    <DownloadSimple size={16} weight="bold" />
                                  </a>
                                  {!isFieldsDisabled && (
                                    <button
                                      type="button"
                                      className="file-action-btn delete"
                                      onClick={() =>
                                        handleDeleteTreatmentFile(file.id)
                                      }
                                      title="מחיקה"
                                    >
                                      <Trash size={16} weight="bold" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Empty State */}
                      {treatmentFiles.length === 0 &&
                        pendingFiles.length === 0 && (
                          <div className="treatment-files-empty">
                            <Paperclip size={24} weight="duotone" />
                            <span>אין קבצים מצורפים</span>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}

              {/* Comments Section */}
              {!isNew && treatmentId !== "new" && (
                <div className="treatment-comments-section">
                  <h3>
                    <ChatCircle size={20} weight="duotone" />
                    הערות ותגובות
                  </h3>

                  <div className="treatment-comments-list">
                    {existingTreatment?.comments?.length > 0 ? (
                      existingTreatment.comments.map((comment) => (
                        <div
                          key={comment.id}
                          className="treatment-comment-item"
                        >
                          <div className="treatment-comment-avatar">
                            {comment.authorName?.charAt(0) || "?"}
                          </div>
                          <div className="treatment-comment-content">
                            <div className="treatment-comment-header">
                              <span className="treatment-comment-author">
                                {comment.authorName}
                              </span>
                              <span className="treatment-comment-date">
                                {formatDateTime(comment.createdAt)}
                              </span>
                            </div>
                            <div className="treatment-comment-text">
                              {comment.content}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="treatment-comment-empty">
                        אין הערות עדיין
                      </div>
                    )}
                  </div>

                  {!isDeleted && !isReadOnlyView && (
                    <div className="treatment-add-comment">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="הוסף הערה..."
                      />
                      <div className="treatment-add-comment-btn-container">
                        <button
                          type="button"
                          className="treatment-add-comment-btn"
                          onClick={handleAddComment}
                          disabled={!newComment.trim() || isAddingComment}
                        >
                          {isAddingComment ? (
                            <LoaderLoop size="xs" />
                          ) : (
                            <Plus size={16} weight="bold" />
                          )}
                          הוסף
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timeline Sidebar */}
        <div className="timeline-sidebar">
          <div className="timeline-header">
            <h3>
              <Clock size={18} weight="duotone" />
              ציר זמן
            </h3>
            {canWriteTreatments !== false && (
            <CustomClinikitActionButton
              size="small"
              icon={<Plus size={14} weight="bold" />}
              onClick={() =>
                navigate(`/dashboard/patients/${patientId}/treatments/new`)
              }
            >
              חדש
            </CustomClinikitActionButton>
            )}
          </div>

          <div className="timeline-list">{timelineListContent}</div>
        </div>
      </div>

      {/* Mobile timeline bottom sheet — same list as the desktop sidebar.
          Opened from the header clock button (mobile only). */}
      {showTimelineSheet && (
        <div
          className="tds-overlay"
          onClick={() => setShowTimelineSheet(false)}
        >
          <div
            className="tds-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="ציר זמן"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tds-grabber" />
            <div className="tds-header">
              <h3>
                <Clock size={18} weight="duotone" />
                ציר זמן
              </h3>
              <div className="tds-header-actions">
                {canWriteTreatments !== false && (
                  <CustomClinikitActionButton
                    size="small"
                    icon={<Plus size={14} weight="bold" />}
                    onClick={() => {
                      setShowTimelineSheet(false);
                      navigate(
                        `/dashboard/patients/${patientId}/treatments/new`,
                      );
                    }}
                  >
                    חדש
                  </CustomClinikitActionButton>
                )}
                <ModalCloseButton
                  onClick={() => setShowTimelineSheet(false)}
                  size={18}
                />
              </div>
            </div>
            <div className="tds-list">{timelineListContent}</div>
          </div>
        </div>
      )}

      {/* Mobile sticky save bar — the primary CTA (and locked-edit actions)
          on ≤768px. Hidden during the setup wizard and in locked view-mode.
          Same handlers + disabled logic as the desktop header CTA. */}
      {showMobileSavebar && (
        <div className="treatment-mobile-savebar">
          {!isEditing && autoSaveStatus && (
            <span className={`treatment-auto-save-indicator ${autoSaveStatus}`}>
              {autoSaveStatus === "saving" ? (
                <>
                  <LoaderLoop size="xs" />
                  שומר...
                </>
              ) : (
                <>
                  <Check size={14} weight="bold" />
                  נשמר אוטומטית
                </>
              )}
            </span>
          )}
          {!isEditing && !formData.treatmentTime && !isSaving && (
            <span className="treatment-disabled-reason">
              <WarningCircle size={12} weight="bold" />
              יש לבחור שעת טיפול
            </span>
          )}
          <div className="tms-actions">
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="treatment-cancel-edit-btn"
                  onClick={handleCancelEdit}
                >
                  <X size={16} weight="bold" />
                  ביטול
                </button>
                <button
                  type="button"
                  className="treatment-save-edit-btn tms-cta"
                  onClick={handleSaveLockedEdit}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <LoaderLoop size="xs" />
                  ) : (
                    <FloppyDisk size={16} weight="bold" />
                  )}
                  שמור שינויים
                </button>
              </>
            ) : (
              <button
                type="button"
                className="treatment-save-btn primary tms-cta"
                onClick={handleOpenSummary}
                disabled={
                  isSaving || isCreatingDraft || !formData.treatmentTime
                }
              >
                {isCreatingDraft ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <FloppyDisk size={18} weight="bold" />
                )}
                <span>
                  {isCreatingDraft ? "יוצר טיוטה..." : "שמור סיכום טיפול"}
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Confirm Replace & Link Modal — triggered when linking an appointment
          that has a live session while the form already contains work. */}
      {pendingAppointmentLink &&
        (() => {
          const liveSession = pendingAppointmentLink.liveSession;
          const filledFields = (liveSession?.fields || []).filter((f) => {
            if (f.value === "" || f.value === null || f.value === undefined)
              return false;
            if (typeof f.value === "boolean") return true;
            return String(f.value).trim() !== "";
          });
          const liveNotes = (liveSession?.liveNotes || []).filter(
            (n) =>
              (n.title && n.title.trim()) || (n.content && n.content.trim()),
          );
          const hasAnyLiveContent =
            filledFields.length > 0 || liveNotes.length > 0;

          return (
            <div className="confirm-lock-modal">
              <div className="confirm-lock-content confirm-live-link">
                <div className="confirm-lock-icon">
                  <LinkSimple size={32} weight="bold" />
                </div>
                <h3>שיוך סיכום לייב</h3>
                <p>
                  לפגישה זו קיים סיכום לייב.
                  <br />
                  שים לב שבזמן שאתה משייך את הפגישה, השדות של הטקסט שכבר מילאת
                  יתחלפו בתוכן שמילאת בזמן שהפגישה התרחשה. אם תבחר לשייך מבלי
                  להחליף — ההערות שכתבת בזמן הפגישה יאבדו.
                </p>

                {hasAnyLiveContent && (
                  <div className="confirm-live-preview">
                    <button
                      type="button"
                      className={`confirm-live-preview-toggle ${
                        showPendingLivePreview ? "open" : ""
                      }`}
                      onClick={() => setShowPendingLivePreview((prev) => !prev)}
                      aria-expanded={showPendingLivePreview}
                    >
                      <CaretDown size={14} weight="bold" />
                      <span>צפה בהערות שכתבתי בזמן הפגישה</span>
                    </button>

                    {showPendingLivePreview && (
                      <div
                        className="confirm-live-preview-body"
                        role="region"
                        aria-label="הערות מהפגישה"
                      >
                        {filledFields.map((f) => (
                          <div
                            key={`field-${f.id}`}
                            className="confirm-live-preview-item"
                          >
                            <div className="confirm-live-preview-title">
                              {f.label || f.id}
                            </div>
                            <div className="confirm-live-preview-value">
                              {typeof f.value === "boolean"
                                ? f.value
                                  ? "כן"
                                  : "לא"
                                : String(f.value)}
                            </div>
                          </div>
                        ))}
                        {liveNotes.map((n, idx) => (
                          <div
                            key={`note-${n.id || idx}`}
                            className="confirm-live-preview-item"
                          >
                            <div className="confirm-live-preview-title">
                              {n.title?.trim() || `הערה ${idx + 1}`}
                            </div>
                            {n.content?.trim() && (
                              <div className="confirm-live-preview-value">
                                {n.content}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="confirm-lock-actions confirm-live-link-actions">
                  <button
                    className="confirm-lock-btn"
                    onClick={handleConfirmReplaceAndLink}
                  >
                    <LinkSimple size={16} weight="bold" />
                    שייך והחלף
                  </button>
                  <button
                    className="cancel-lock-btn link-without-replace-btn"
                    onClick={handleLinkWithoutReplacing}
                  >
                    שייך את הפגישה מבלי להחליף את השדות
                  </button>
                  <button
                    className="cancel-lock-btn"
                    onClick={handleCancelReplaceAndLink}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Confirm Delete Modal */}
      {showConfirmDelete && (
        <div className="confirm-delete-modal">
          <div className="confirm-delete-content">
            <div className="confirm-delete-icon">
              <Trash size={32} weight="bold" />
            </div>
            <h3>מחיקת טיוטא</h3>
            <p>
              האם אתה בטוח שברצונך למחוק את הטיוטא?
              <br />
              פעולה זו תחזיר אותך לעמוד המטופל.
            </p>
            <div className="confirm-delete-actions">
              <button
                className="cancel-delete-btn"
                onClick={() => setShowConfirmDelete(false)}
                disabled={isDeleting}
              >
                ביטול
              </button>
              <button
                className="confirm-delete-btn"
                onClick={handleDeleteDraft}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <Trash size={16} weight="bold" />
                )}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Soft Delete (locked reports) */}
      <ConfirmDialog
        isOpen={showConfirmSoftDelete}
        onClose={() => {
          setShowConfirmSoftDelete(false);
          setSoftDeleteReason("");
        }}
        onConfirm={handleSoftDelete}
        type="danger"
        icon="delete"
        title="מחיקת דוח טיפול נעול"
        message="דוחות טיפול הם רשומה רפואית ולכן לעולם אינם נמחקים מהמערכת. הדוח יסומן כמחוק: הוא לא ייחשב עוד כדוח תקף או פעיל, לא ייכלל בסיכומי ה-AI, בדוחות ובציר הזמן של המטופל, והפגישה המקושרת תחזור להופיע כלא מתועדת. עותק שלו יישמר בגיבויים לצרכים משפטיים/רגולטוריים. לא ניתן לשחזר דוח שנמחק."
        confirmText="סמן כמחוק"
        cancelText="ביטול"
        confirmDisabled={isSoftDeleting}
      >
        <label className="tdp-delete-reason-label" htmlFor="tdp-delete-reason">
          סיבת המחיקה (לא חובה)
        </label>
        <textarea
          id="tdp-delete-reason"
          className="tdp-delete-reason"
          rows={3}
          maxLength={300}
          value={softDeleteReason}
          onChange={(e) => setSoftDeleteReason(e.target.value)}
          placeholder="לדוגמה: תועד על מטופל שגוי / תיעוד כפול"
        />
      </ConfirmDialog>

      {/* Unified Summary Popup
          Opens from the primary "שמור סיכום טיפול" CTA. Drives 4 views:
          - summary:    compact read-only preview of questions + answers
          - ai-loading: AI enrichment in progress
          - ai-preview: enriched vs original toggle, lock from here
          - ai-error:   enrichment failed, retry or go back                */}
      {showSummaryModal && (
        <div className="summary-modal-overlay" onClick={handleCloseSummary}>
          <div
            className="summary-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="summary-modal-header">
              <div className="summary-modal-title">
                {summaryModalView === "summary" && (
                  <FileText size={22} weight="duotone" />
                )}
                {(summaryModalView === "ai-loading" ||
                  summaryModalView === "ai-preview" ||
                  summaryModalView === "ai-error") && (
                  <MagicWand size={22} weight="fill" />
                )}
                <h3>
                  {summaryModalView === "summary" && "סיכום טיפול"}
                  {summaryModalView === "ai-loading" && "העשרת טקסט AI"}
                  {summaryModalView === "ai-preview" && "תצוגת טקסט מועשר"}
                  {summaryModalView === "ai-error" && "שגיאה בהעשרת הטקסט"}
                </h3>
              </div>
              <ModalCloseButton onClick={handleCloseSummary} />
            </div>

            <div className="summary-modal-content">
              {/* ----- Summary view ----- */}
              {summaryModalView === "summary" && (
                <>
                  <div className="summary-modal-meta">
                    <span>
                      <CalendarBlank size={14} weight="bold" />
                      {formatDate(formData.treatmentDate)}
                    </span>
                    {formData.treatmentTime && (
                      <span>
                        <Clock size={14} weight="bold" />
                        {formData.treatmentTime}
                      </span>
                    )}
                  </div>

                  <div className="summary-modal-fields">
                    {formData.fields.length === 0 && !formData.content && (
                      <div className="summary-modal-empty">
                        <FileText size={28} weight="duotone" />
                        <span>אין עדיין תוכן לסיכום</span>
                      </div>
                    )}

                    {formData.fields.map((field) => {
                      if (field.type === "section_header") {
                        return (
                          <div
                            key={field.id}
                            className="summary-modal-section-header"
                          >
                            {field.label}
                          </div>
                        );
                      }
                      const rawValue = field.value;
                      const isMulti = field.type === "multi_select";
                      const hasValue =
                        field.type === "checkbox"
                          ? true
                          : isMulti
                            ? Array.isArray(rawValue) && rawValue.length > 0
                            : rawValue && String(rawValue).trim() !== "";
                      // long_text is backed by RichTextEditor and stores
                      // HTML (contenteditable output — <div>, <br>, <p>,
                      // &nbsp; etc.). Render as HTML so line breaks and
                      // block tags display correctly instead of showing
                      // raw markup.
                      const isRich = field.type === "long_text";
                      return (
                        <div key={field.id} className="summary-modal-field">
                          <label>{field.label}</label>
                          {field.type === "checkbox" ? (
                            <div className="summary-modal-field-value">
                              {rawValue ? "כן" : "לא"}
                            </div>
                          ) : !hasValue ? (
                            <div className="summary-modal-field-value">
                              <em className="summary-modal-empty-value">—</em>
                            </div>
                          ) : isRich ? (
                            <div
                              className="summary-modal-field-value summary-modal-rich"
                              dangerouslySetInnerHTML={{
                                __html: String(rawValue),
                              }}
                            />
                          ) : (
                            <div className="summary-modal-field-value">
                              {isMulti ? rawValue.join(", ") : rawValue}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {formData.content && (
                      <div className="summary-modal-field">
                        <label>תוכן נוסף</label>
                        <div
                          className="summary-modal-field-value summary-modal-rich"
                          dangerouslySetInnerHTML={{ __html: formData.content }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ----- AI loading view ----- */}
              {summaryModalView === "ai-loading" && (
                <div className="summary-modal-ai-loading">
                  <div className="summary-modal-ai-sparkles">
                    <Sparkle size={48} weight="fill" className="sparkle-1" />
                    <Sparkle size={32} weight="fill" className="sparkle-2" />
                    <Sparkle size={24} weight="fill" className="sparkle-3" />
                  </div>
                  <LoaderLoop size="md" />
                  <span>ה-AI מעשיר את הטקסט...</span>
                  <p>זה עשוי לקחת מספר שניות</p>
                </div>
              )}

              {/* ----- AI preview view (enriched vs original) ----- */}
              {summaryModalView === "ai-preview" && aiEnrichedData && (
                <>
                  <div className="summary-modal-ai-banner">
                    <Warning size={18} weight="fill" />
                    <span>
                      <strong>טקסט מועשר ע״י AI</strong> — ניתן להחליף לתצוגת
                      המקור שלך. בלחיצה על "נעל ושמור" יישמר הטקסט שמוצג כעת,
                      והגרסה המקורית תישמר בהיסטוריית הגרסאות.
                    </span>
                  </div>

                  {/* Segmented toggle — clearly communicates that both the
                      enriched and original texts are available to preview
                      (and which one is currently shown). */}
                  <div className="summary-modal-toggle-wrapper">
                    <div
                      className="summary-modal-toggle-label"
                      id="ai-preview-toggle-label"
                    >
                      בחר גרסה להצגה:
                    </div>
                    <div
                      className={`summary-modal-toggle ${aiPreviewTab === "original" ? "toggle-second" : "toggle-first"}`}
                      role="tablist"
                      aria-labelledby="ai-preview-toggle-label"
                    >
                      <span
                        className="summary-modal-toggle-indicator"
                        aria-hidden="true"
                      />
                      <button
                        type="button"
                        role="tab"
                        aria-selected={aiPreviewTab === "enriched"}
                        className={`summary-modal-toggle-option ${aiPreviewTab === "enriched" ? "active" : ""}`}
                        onClick={() => setAiPreviewTab("enriched")}
                      >
                        <MagicWand size={16} weight="fill" />
                        <span>טקסט מועשר AI</span>
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={aiPreviewTab === "original"}
                        className={`summary-modal-toggle-option ${aiPreviewTab === "original" ? "active" : ""}`}
                        onClick={() => setAiPreviewTab("original")}
                      >
                        <PencilSimple size={16} weight="fill" />
                        <span>הטקסט המקורי שלי</span>
                      </button>
                    </div>
                  </div>

                  <div className="summary-modal-fields">
                    {formData.fields
                      .filter(
                        (f) =>
                          (f.type === "short_text" || f.type === "long_text") &&
                          (aiEnrichedData.enrichedFields?.[f.id] !==
                            undefined ||
                            aiEnrichedData.originalFields?.[f.id] !==
                              undefined),
                      )
                      .map((field) => {
                        const enriched =
                          aiEnrichedData.enrichedFields?.[field.id];
                        const original =
                          aiEnrichedData.originalFields?.[field.id] ??
                          field.value;
                        const shown =
                          aiPreviewTab === "enriched" ? enriched : original;
                        const hasValue = shown && String(shown).trim() !== "";
                        const isRich = field.type === "long_text";
                        return (
                          <div key={field.id} className="summary-modal-field">
                            <label>{field.label}</label>
                            {!hasValue ? (
                              <div className="summary-modal-field-value">
                                <em className="summary-modal-empty-value">—</em>
                              </div>
                            ) : isRich ? (
                              <div
                                className="summary-modal-field-value summary-modal-rich"
                                dangerouslySetInnerHTML={{
                                  __html: String(shown),
                                }}
                              />
                            ) : (
                              <div className="summary-modal-field-value">
                                {shown}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </>
              )}

              {/* ----- AI error view ----- */}
              {summaryModalView === "ai-error" && (
                <div className="summary-modal-ai-error">
                  <Warning size={48} weight="fill" />
                  <h4>שגיאה בהעשרת הטקסט</h4>
                  <p>{aiEnrichmentError}</p>
                  <p className="summary-modal-ai-support">
                    אם הבעיה נמשכת, אנא פנה לתמיכה
                  </p>
                </div>
              )}
            </div>

            <div className="summary-modal-actions">
              {summaryModalView === "summary" && (
                <>
                  <button
                    type="button"
                    className="summary-modal-btn-tertiary"
                    onClick={handleCloseSummary}
                  >
                    חזרה לעריכה
                  </button>
                  <button
                    type="button"
                    className="summary-modal-btn-secondary"
                    onClick={handleAIEnrichInPopup}
                    disabled={
                      isSaving ||
                      !formData.fields.some(
                        (f) =>
                          (f.type === "short_text" || f.type === "long_text") &&
                          f.value?.trim(),
                      )
                    }
                  >
                    <MagicWand size={18} weight="bold" />
                    העשרת טקסט AI
                  </button>
                  <button
                    type="button"
                    className="summary-modal-btn-primary"
                    onClick={handleLockFromPopup}
                    disabled={isSaving}
                  >
                    <Lock size={18} weight="bold" />
                    נעל ושמור
                  </button>
                </>
              )}

              {summaryModalView === "ai-preview" && (
                <>
                  <button
                    type="button"
                    className="summary-modal-btn-tertiary"
                    onClick={() => setSummaryModalView("summary")}
                  >
                    <ArrowRight size={16} weight="bold" />
                    חזרה לסיכום
                  </button>
                  <button
                    type="button"
                    className="summary-modal-btn-primary"
                    onClick={handleLockFromAIPreview}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <LoaderLoop size="xs" />
                    ) : (
                      <Lock size={18} weight="bold" />
                    )}
                    {aiPreviewTab === "enriched"
                      ? "נעל ושמור עם טקסט מועשר"
                      : "נעל ושמור"}
                  </button>
                </>
              )}

              {summaryModalView === "ai-error" && (
                <>
                  <button
                    type="button"
                    className="summary-modal-btn-tertiary"
                    onClick={() => setSummaryModalView("summary")}
                  >
                    <ArrowRight size={16} weight="bold" />
                    חזרה לסיכום
                  </button>
                  {aiRetryAvailable && (
                    <button
                      type="button"
                      className="summary-modal-btn-secondary"
                      onClick={handleAIRetryInPopup}
                    >
                      <ArrowClockwise size={16} weight="bold" />
                      נסה שוב
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Edit Modal */}
      {showConfirmEdit && (
        <div className="confirm-lock-modal">
          <div className="confirm-lock-content">
            <div className="confirm-lock-icon confirm-edit-icon">
              <PencilSimple size={32} weight="bold" />
            </div>
            <h3>עריכת תיעוד נעול</h3>
            <p>
              הגרסה הנוכחית תישמר באופן אוטומטי.
              <br />
              לאחר השמירה ניתן יהיה לצפות בגרסאות קודמות.
            </p>
            <div className="confirm-lock-actions">
              <button
                className="cancel-lock-btn"
                onClick={() => setShowConfirmEdit(false)}
              >
                ביטול
              </button>
              <button className="confirm-edit-btn" onClick={handleStartEdit}>
                <PencilSimple size={16} weight="bold" />
                התחל עריכה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="confirm-lock-modal">
          <div className="version-history-modal-content">
            <div className="version-history-modal-header">
              <h3>
                <ClockCounterClockwise size={20} weight="bold" />
                היסטוריית גרסאות
              </h3>
              <button
                type="button"
                className="version-history-close-btn"
                onClick={() => {
                  setShowVersionHistory(false);
                  setViewingVersion(null);
                }}
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {viewingVersion ? (
              <div className="version-detail">
                <div className="version-detail-header">
                  <button
                    type="button"
                    className="version-back-btn"
                    onClick={() => setViewingVersion(null)}
                  >
                    <ArrowRight size={16} weight="bold" />
                    חזרה לרשימה
                  </button>
                  <span className="version-detail-badge">
                    גרסה {viewingVersion.versionNumber}
                  </span>
                </div>
                <div className="version-detail-meta">
                  <span>
                    {viewingVersion.savedAt
                      ? formatDateTime(viewingVersion.savedAt)
                      : ""}
                  </span>
                  <span>נערך ע״י {viewingVersion.savedByName}</span>
                </div>
                <div className="version-detail-fields">
                  {(viewingVersion.fields || []).map((field) => {
                    if (field.type === "section_header") {
                      return (
                        <div
                          key={field.id}
                          className="summary-modal-section-header"
                        >
                          {field.label}
                        </div>
                      );
                    }
                    return (
                      <div key={field.id} className="version-field">
                        <label>{field.label}</label>
                        <div className="version-field-value">
                          {field.type === "checkbox"
                            ? field.value
                              ? "כן"
                              : "לא"
                            : field.type === "multi_select"
                              ? Array.isArray(field.value) &&
                                field.value.length > 0
                                ? field.value.join(", ")
                                : "—"
                              : field.value || "—"}
                        </div>
                      </div>
                    );
                  })}
                  {viewingVersion.content && (
                    <div className="version-field">
                      <label>תוכן חופשי</label>
                      <div className="version-field-value">
                        {viewingVersion.content}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="version-list">
                <div className="version-list-item version-current">
                  <div className="version-list-dot current" />
                  <div className="version-list-info">
                    <span className="version-list-label">
                      גרסה {existingTreatment?.currentVersion || 1} (נוכחית)
                    </span>
                    <span className="version-list-date">
                      {existingTreatment?.lastEditedAt
                        ? formatDateTime(existingTreatment.lastEditedAt)
                        : formatDateTime(existingTreatment?.updatedAt)}
                    </span>
                  </div>
                </div>
                {treatmentVersions.map((version) => (
                  <div
                    key={version.id}
                    className="version-list-item"
                    onClick={() => setViewingVersion(version)}
                  >
                    <div className="version-list-dot" />
                    <div className="version-list-info">
                      <span className="version-list-label">
                        גרסה {version.versionNumber}
                      </span>
                      <span className="version-list-date">
                        {version.savedAt ? formatDateTime(version.savedAt) : ""}
                      </span>
                      <span className="version-list-author">
                        {version.savedByName}
                      </span>
                    </div>
                    <ArrowLeft
                      size={14}
                      weight="bold"
                      className="version-list-arrow"
                    />
                  </div>
                ))}
                {treatmentVersions.length === 0 && (
                  <div className="version-list-empty">אין גרסאות קודמות</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick actions FAB — only shown while the form is editable and the
          setup wizard is done (copy-previous would edit fields that are
          hidden during setup). Groups "quick add task" and "copy previous
          summary" so they don't take any header real-estate. */}
      {!isFieldsDisabled && !showSetupWizard && (
        <div className="treatment-fab-wrapper">
          {showQuickActions && (
            <div
              className="treatment-fab-backdrop"
              onClick={() => setShowQuickActions(false)}
            />
          )}
          <div
            className={`treatment-fab-container ${
              showQuickActions ? "open" : ""
            }`}
          >
            {showQuickActions && (
              <div className="treatment-fab-menu" role="menu">
                <button
                  type="button"
                  className="treatment-fab-action"
                  onClick={handleOpenQuickTask}
                  role="menuitem"
                >
                  <span className="treatment-fab-action-icon">
                    <CheckSquare size={18} weight="bold" />
                  </span>
                  <span className="treatment-fab-action-label">
                    משימה מהירה
                  </span>
                </button>
                <button
                  type="button"
                  className="treatment-fab-action"
                  onClick={handleCopyPrevious}
                  disabled={!previousTreatment}
                  title={
                    previousTreatment ? undefined : "אין סיכום קודם להעתקה"
                  }
                  role="menuitem"
                >
                  <span className="treatment-fab-action-icon">
                    <Copy size={18} weight="bold" />
                  </span>
                  <span className="treatment-fab-action-label">
                    העתק מסיכום קודם
                  </span>
                </button>
              </div>
            )}
            <button
              type="button"
              className={`treatment-fab-trigger ${
                showQuickActions ? "open" : ""
              }`}
              onClick={() => setShowQuickActions((prev) => !prev)}
              aria-label={
                showQuickActions ? "סגירת פעולות מהירות" : "פעולות מהירות"
              }
              aria-expanded={showQuickActions}
            >
              {showQuickActions ? (
                <X size={22} weight="bold" />
              ) : (
                <Plus size={22} weight="bold" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Quick Task Modal — small popup-style task creation auto-linked
          to the current patient. Defaults to personal scope + assigned to
          the current therapist. */}
      {showQuickTaskModal && (
        <div
          className="quick-task-modal-overlay"
          onClick={handleCloseQuickTaskModal}
        >
          <div
            className="quick-task-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quick-task-modal-header">
              <h3>משימה מהירה</h3>
              <ModalCloseButton onClick={handleCloseQuickTaskModal} />
            </div>

            <div className="quick-task-modal-body">
              <div className="quick-task-field">
                <label className="quick-task-label required">כותרת</label>
                <input
                  type="text"
                  className="quick-task-input"
                  value={quickTaskForm.title}
                  onChange={(e) =>
                    setQuickTaskForm((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  placeholder="הכנס כותרת למשימה"
                  autoFocus
                />
              </div>

              <div className="quick-task-field">
                <label className="quick-task-label">הערה</label>
                <textarea
                  className="quick-task-input quick-task-textarea"
                  value={quickTaskForm.note}
                  onChange={(e) =>
                    setQuickTaskForm((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                  }
                  placeholder="הוסף הערה (אופציונלי)"
                  rows={3}
                />
              </div>

              <div className="quick-task-row">
                <div className="quick-task-field">
                  <label className="quick-task-label">תאריך יעד</label>
                  <DatePicker
                    value={quickTaskForm.dueDate}
                    onChange={(e) =>
                      setQuickTaskForm((prev) => ({
                        ...prev,
                        dueDate: e.target.value,
                        dueTime: e.target.value ? prev.dueTime : "",
                      }))
                    }
                  />
                </div>

                <div className="quick-task-field">
                  <label className="quick-task-label">שעת יעד</label>
                  <TimePicker
                    value={quickTaskForm.dueTime}
                    disabled={!quickTaskForm.dueDate}
                    onChange={(e) =>
                      setQuickTaskForm((prev) => ({
                        ...prev,
                        dueTime: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {taskCategories.length > 0 && (
                <div className="quick-task-field">
                  <label className="quick-task-label">קטגוריה</label>
                  <select
                    className="quick-task-input"
                    value={quickTaskForm.categoryId}
                    onChange={(e) =>
                      setQuickTaskForm((prev) => ({
                        ...prev,
                        categoryId: e.target.value,
                      }))
                    }
                  >
                    <option value="">ללא קטגוריה</option>
                    {taskCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="quick-task-modal-footer">
              <button
                type="button"
                className="quick-task-btn quick-task-btn-secondary"
                onClick={handleCloseQuickTaskModal}
                disabled={isCreatingTask}
              >
                ביטול
              </button>
              <button
                type="button"
                className="quick-task-btn quick-task-btn-primary"
                onClick={handleSaveQuickTask}
                disabled={!quickTaskForm.title.trim() || isCreatingTask}
              >
                {isCreatingTask && (
                  <LoaderLoop size="xs" />
                )}
                <span>צור משימה</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Copy Previous — warns before overwriting existing content. */}
      {showConfirmCopyPrevious && (
        <div className="confirm-lock-modal">
          <div className="confirm-lock-content">
            <div className="confirm-lock-icon">
              <Copy size={32} weight="bold" />
            </div>
            <h3>העתקת סיכום קודם</h3>
            <p>
              התוכן הקיים בטופס יוחלף בתוכן מהסיכום הקודם
              {previousTreatment?.treatmentDate
                ? ` מתאריך ${formatDate(previousTreatment.treatmentDate)}`
                : ""}
              . האם להמשיך?
            </p>
            <div className="confirm-lock-actions">
              <button
                type="button"
                className="cancel-lock-btn"
                onClick={() => setShowConfirmCopyPrevious(false)}
              >
                ביטול
              </button>
              <button
                type="button"
                className="confirm-lock-btn"
                onClick={performCopyPrevious}
              >
                <Copy size={16} weight="bold" />
                העתק
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI chat — conversation persists on the treatment doc, so the dialog
          can be closed and reopened without losing anything. */}
      <AiChatDialog
        open={showAiChat}
        onClose={() => setShowAiChat(false)}
        messages={aiChatMessages}
        isLoading={aiChatLoading}
        error={aiChatError}
        onSendMessage={handleAiChatSend}
        onInsertText={handleAiChatInsert}
        onRetry={handleAiChatRetry}
      />
    </div>
  );
}

export default TreatmentDocPage;
